<!-- Copyright (c) 2026 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="manage-main">
    <div class="left-menu">
      <Menus :key="manageMenuRenderKey" :menu="visibleMenu" @menuClicked="menuClicked" />
    </div>

    <div class="manage-workspace">
      <div v-if="isHMS && activePage == 'coefficientSettings'" class="page-content coefficient-page">
        <div class="content-header">
          <div>Coefficient Settings</div>
        </div>

        <div class="coefficient-top">
          <BoxContainer class="price-panel" title="Price Coefficient">
            <div class="table-wrap cipl-table-wrap">
              <table class="data-table price-grid">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Job Type</th>
                    <th>Price Coefficient</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in priceCoefficients" :key="row['#']">
                    <td>{{ row['#'] }}</td>
                    <td>{{ row['Job Type'] }}</td>
                    <td>
                      <Input
                        placeholder=""
                        :inputValue.sync="row['Price Coefficient']"
                        :isDisabled="!editMode.coefficientSettings"
                        validator="positiveDecimal,11,5"
                        @blur="formatPriceCoefficient(row)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <RadioButtonSet
              title="Use Price Coeff for Driver Inc?"
              :buttons="driverCoeffOptions"
              labelKey="label"
              :selected.sync="selectedDriverCoeffMode"
              :isHorizontal="true"
              :showBox="false"
              :isDisabled="!editMode.coefficientSettings"
            />
          </BoxContainer>

          <BoxContainer class="movement-panel" title="Movement Type Coefficient">
            <div class="table-wrap cipl-table-wrap">
              <table class="data-table incentive-grid coefficient-grid">
                <thead>
                  <tr>
                    <th>Job Type</th>

                    <th v-for="movement in movementTypes" :key="movement">
                      {{ movement }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-if="coefficientLoading" class="table-message-row">
                    <td :colspan="movementTypes.length + 1" class="table-message-cell">
                      Loading Movement Type Coefficient data . . .
                    </td>
                  </tr>

                  <tr v-else-if="movementCoefficients.length == 0" class="table-message-row">
                    <td :colspan="movementTypes.length + 1" class="table-message-cell"> No items </td>
                  </tr>

                  <tr v-else v-for="row in movementCoefficients" :key="row.JobTypeCode">
                    <td>{{ row['Job Type'] }}</td>

                    <td v-for="movement in movementTypes" :key="movement">
                      <Input
                        placeholder=""
                        :inputValue.sync="row[movement].value"
                        :isDisabled="row[movement].disabled || !editMode.coefficientSettings"
                        validator="positiveDecimal,11,5"
                        @blur="formatCoefficientCell(row[movement])"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </BoxContainer>
        </div>

        <div class="bottom-actions">
          <template v-if="!editMode.coefficientSettings">
            <button :disabled="coefficientLoading" @click="editCoefficientSettings"> Edit </button>
          </template>

          <template v-else>
            <button @click="saveCoefficientSettings"> Save Edit </button>
            <button @click="cancelCoefficientSettings"> Cancel </button>
          </template>
        </div>
      </div>

      <div v-if="activePage == 'driverIncentive'" class="page-content">
        <div class="content-header">
          <div>Driver Incentive</div>
        </div>

        <div v-if="driverTabIndex != 0 && !isTMS" class="top-selector">
          <InputQuery
            placeholder="Commission Code"
            :items="commissionList"
            itemKey="Code - Description"
            :selectedObject.sync="selectedCommission"
            :isDisabled="tableLoading || tmsFixedEditing || editMode.driverCommission || editMode.revenueMovement || editMode.nonRevenue || editMode.fixedZone || editMode.incremental"
            @selected="commissionSelected"
          />
        </div>

        <div v-else class="top-selector driver-commission-selector" :class="{ 'tms-commission-selector': isTMS }">
          <!-- for Edit -->
          <InputQuery
            v-if="formMode.driverCommission != 'new'"
            key="driver-commission-existing"
            class="commission-code-input"
            placeholder="Commission Code"
            :items="driverCommissionList"
            itemKey="Com Code"
            :selectedObject.sync="selectedDriverCommission"
            :isDisabled="tableLoading || tmsFixedEditing || editMode.driverCommission || editMode.revenueMovement || editMode.nonRevenue || editMode.fixedZone || editMode.incremental"
            @selected="driverCommissionSelected"
          />

          <!-- for New -->
          <InputQuery
            v-else
            key="driver-commission-new"
            class="commission-code-input"
            placeholder="Commission Code"
            :items="[]"
            itemKey="Com Code"
            :search.sync="driverCommissionCode"
            :allowFreeText="true"
            validator="text,10,uppercase,alphanumeric"
          />
          <Input
            class="commission-description-input"
            placeholder="Description"
            :inputValue.sync="driverCommissionDescription"
            :isDisabled="!editMode.driverCommission"
          />
          <Dropdown
            v-if="isTMS"
            class="commission-range-type"
            placeholder="Range Type"
            :items="tmsRangeTypes"
            itemKey="RangeDescription"
            :selectedObject.sync="selectedTMSRangeType"
            :isDisabled="tableLoading || !coefficientSettingsDetail.ComHeader || !editMode.driverCommission"
          />
        </div>
        <Tab :key="driverTabRenderKey" class="driver-incentive-tab" :tabs="visibleDriverIncentiveTabs" :tabIndex="driverTabIndex" @update:tabIndex="driverTabChanged">
          <div slot="Driver Commission Structure" class="driver-tab-page">
            <BoxContainer
              v-if="coefficientSettingsDetail.ComHeader"
              class="coefficient-settings-detail coefficient-settings-bottom"
              :title="
                formMode.driverCommission == 'new' ? 'New Driver Commission Structure'
                : formMode.driverCommission == 'edit' ? 'Edit Driver Commission Structure'
                : 'Driver Commission Structure'
              "
            >
              <div v-if="isHMS" class="field-grid three">
                <Input
                  placeholder="Driver Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay1"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
                <Input
                  placeholder="Attendant Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay2"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
              </div>

              <div v-if="isTMS" class="field-grid three" style="padding-bottom:5px;">
                <Dropdown
                  placeholder="Basic Pay Type"
                  :items="tmsBasicPayTypeOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedTMSBasicPayType"
                  :isDisabled="!editMode.driverCommission"
                />
                <Input
                  placeholder="Driver Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay1"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
                <Input
                  placeholder="Attendant Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay2"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
              </div>

              <div v-if="isTMS" class="tms-trip-commission-grid">
                <div class="tms-trip-commission-field">
                  <Input
                    placeholder="Driver Per Trip (Delivery/ Collection) Commission"
                    :inputValue.sync="coefficientSettingsDetail.ComHeader.TripCommRate1"
                    validator="positiveDecimal,19,5"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Dropdown
                    placeholder="Type"
                    :items="tmsTripRateTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedTMSTripDriverType"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
                <div class="tms-trip-commission-field">
                  <Input
                    placeholder="Attendant Per Trip (Delivery/ Collection) Commission"
                    :inputValue.sync="coefficientSettingsDetail.ComHeader.TripCommRate2"
                    validator="positiveDecimal,19,5"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Dropdown
                    placeholder="Type"
                    :items="tmsTripRateTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedTMSTripAttendantType"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
              </div>

              <BoxContainer v-if="isTMS" title="Additional Commission by Revenue Range">
                <div class="tms-revenue-inputs">
                  <div class="tms-revenue-row">
                    <span>From (RM)</span>
                    <span></span>
                    <span>To (RM)</span>
                    <span>Driver (%)</span>
                    <span>Attendant (%)</span>
                  </div>
                  <div
                    v-for="(row, index) in tmsRevenueRanges"
                    :key="row.Seq_No"
                    class="tms-revenue-row"
                  >
                    <Input
                      placeholder=""
                      :inputValue="tmsRevenueRangeFrom(index)"
                      :isDisabled="true"
                    />
                    <span>to</span>
                    <Input
                      placeholder=""
                      :inputValue.sync="row.Range"
                      validator="positiveDecimal,19,2"
                      :isDisabled="!editMode.driverCommission"
                    />
                    <Input
                      placeholder=""
                      :inputValue.sync="row.Driver"
                      validator="positiveDecimal,19,5"
                      :isDisabled="!editMode.driverCommission"
                    />
                    <Input
                      placeholder=""
                      :inputValue.sync="row.Attendant"
                      validator="positiveDecimal,19,5"
                      :isDisabled="!editMode.driverCommission"
                    />
                  </div>
                </div>
              </BoxContainer>

              <BoxContainer title="Monthly Bonus Incentive">
                <div class="formula-row">
                  <span>If comm. RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Range"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span>every RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.IncEvery"
                    validator="positiveDecimal,11,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Input
                    class="formula-input"
                    placeholder="Driver"
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Driver"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.monthlyBonus.Qlf }} </span>
                  <Input
                    class="formula-input"
                    placeholder="Attendant"
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Attendant"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.monthlyBonus.Qlf }} </span>
                  <Dropdown
                    class="formula-dropdown"
                    placeholder="Type"
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedMonthlyType"
                    @selected="commissionTypeSelected('monthlyBonus', $event)"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
              </BoxContainer>

              <BoxContainer title="Reserve (Tabung)">
                <div class="formula-row">
                  <span>If comm. RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.reserve.Range"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span>every RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.reserve.IncEvery"
                    validator="positiveDecimal,11,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Input
                    class="formula-input"
                    placeholder="Driver"
                    :inputValue.sync="coefficientSettingsDetail.reserve.Driver"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.reserve.Qlf }} </span>
                  <Input
                    class="formula-input"
                    placeholder="Attendant"
                    :inputValue.sync="coefficientSettingsDetail.reserve.Attendant"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.reserve.Qlf }} </span>
                  <Dropdown
                    placeholder="Type"
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedReserveType"
                    @selected="commissionTypeSelected('reserve', $event)"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
              </BoxContainer>

              <div v-if="isHMS" class="use-bill-amount-charges">
                <Filters
                  class="use-bill-amt-filter"
                  :isDisabled="!editMode.driverCommission"
                  placeholder="Billing Charge Codes"
                  title="Billing Amount Charge Codes"
                  itemKey="ChgCode"
                  :tableData="billChargeOptions"
                  :selectedObject.sync="selectedUseBillAmtChg"
                  noneSelectedText="None"
                  noneSelectedConfirmButtonText="Select None"
                  itemDesc="ChgCode"
                />
                <Input
                  class="formula-input"
                  placeholder="B2B Coeff"
                  :inputValue.sync="B2BCoeff"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
              </div>
            </BoxContainer>
            <div v-else class="commission-loading"> Loading commission details . . . </div>

            <div class="bottom-actions">
              <template v-if="!editMode.driverCommission">
                <button :disabled="tableLoading" @click="newDriverCommission"> New </button>
                <button :disabled="!coefficientSettingsDetail.ComHeader" @click="editDriverCommission"> Edit </button>
                <button :disabled="!coefficientSettingsDetail.ComHeader" @click="deleteDriverCommission"> Delete </button>
              </template>

              <template v-else>
                <button @click="saveDriverCommission">
                  {{ formMode.driverCommission == 'new' ? 'Save New' : 'Save Edit' }}
                </button>

                <button @click="cancelDriverCommission"> Cancel </button>
              </template>
            </div>
          </div>

          <div v-if="isHMS" slot="Revenue Movement Incentive" class="driver-tab-page">
            <BoxContainer class="fill-panel" title="Revenue Movement Incentive Coefficient">
              <div class="table-wrap cipl-table-wrap">
                <table class="data-table incentive-grid">
                  <thead>
                    <tr>
                      <th>Job Type</th>
                      <th v-for="movement in movementTypes" :key="movement">{{ movement }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="tableLoading" class="table-message-row">
                      <td :colspan="movementTypes.length + 1" class="table-message-cell">
                        Loading Revenue Movement Incentive Coefficient data . . .
                      </td>
                    </tr>

                    <tr v-else-if="revMvtIncDet.length == 0" class="table-message-row">
                      <td :colspan="movementTypes.length + 1" class="table-message-cell"> No items </td>
                    </tr>

                    <tr v-else v-for="row in revMvtIncDet" :key="row['Job Type']">
                      <td>{{ row['Job Type'] }}</td>

                      <td v-for="movement in movementTypes" :key="movement">
                        <div class="amount-cell">
                          <Input
                            placeholder=""
                            :inputValue.sync="row[movement].value"
                            :isDisabled="row[movement].disabled || !editMode.revenueMovement"
                            :validator="getAmountValidator(row[movement])"
                            @blur="formatAmountCell(row[movement])"
                          />

                          <Dropdown
                            :class="['type-dropdown', { 'dropdown-open-left': isLastMovement(movement) }]"
                            placeholder=""
                            :items="amountTypeOptions"
                            itemKey="label"
                            :selectedObject.sync="row[movement].selectedType"
                            :isDisabled="row[movement].disabled || !editMode.revenueMovement"
                            @selected="amountTypeSelected(row[movement], $event)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="diversion-row">
                <label v-for="d in diversionDet" :key="d.key">
                  <span>{{ d.key }}</span>

                  <Input
                    placeholder=""
                    :inputValue.sync="d.value"
                    :isDisabled="tableLoading || !editMode.revenueMovement"
                    :validator="getAmountValidator(d)"
                    @blur="formatAmountCell(d)"
                  />

                  <Dropdown
                    placeholder=""
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="d.selectedType"
                    :isDisabled="tableLoading || !editMode.revenueMovement"
                    @selected="amountTypeSelected(d, $event)"
                  />
                </label>
              </div>
            </BoxContainer>

            <div class="bottom-actions">
              <template v-if="!editMode.revenueMovement">
                <button :disabled="tableLoading || !selectedCommission" @click="editRevenueMovement"> Edit </button>
              </template>

              <template v-else>
                <button @click="saveRevenueMovement">
                  {{ formMode.revenueMovement == 'new' ? 'Save New' : 'Save Edit' }}
                </button>

                <button @click="cancelRevenueMovement"> Cancel </button>
              </template>
            </div>
          </div>

          <div v-if="isHMS" slot="Non-Rev Mvt Inc (Fix)" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Non-Revenue Movement Incentive"
                  :tableData.sync="nonRevIncList"
                  :selectedRow.sync="selectedNonRevenueRows"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.nonRevenue" @click="addRow('nonRevenue')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.nonRevenue"
                  class="details-panel"
                  :title="formMode.nonRevenue == 'new' ? 'New Non-Revenue Details' : 'Edit Non-Revenue Details'"
                >
                  <div class="details-form">
                    <Input
                      placeholder="Movement Type"
                      :inputValue.sync="selectedNonRevenue['Movement']"
                      :isDisabled="formMode.nonRevenue == 'edit'"
                    />
                    <Input placeholder="Description" :inputValue.sync="selectedNonRevenue['Description']" />
                    <div class="details-rate-row">
                      <span>Driver</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedNonRevenue['Driver Rate($)']"
                        validator="postiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedNonRevenue['Driver Rate(%)']"
                        validator="postiveDecimal,11,2"
                      />
                      <div style="grid-column: 2 / -1; width: 75%">
                        <Dropdown
                          placeholder="From/To Tariff"
                          style="width: 100%"
                          :items="tariffDropdown"
                          itemKey="text"
                          :selectedObject.sync="selectedNonRevenueTariff"
                        />
                      </div>
                    </div>
                    <div class="details-rate-row">
                      <span>Attendant</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedNonRevenue['Attendant Rate($)']"
                        validator="postiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedNonRevenue['Attendant Rate(%)']"
                        validator="postiveDecimal,11,2"
                      />
                      <div style="grid-column: 2 / -1; width: 75%">
                        <Dropdown
                          placeholder="From/To Tariff"
                          :items="tariffDropdown"
                          itemKey="text"
                          :selectedObject.sync="selectedNonRevenueAttTariff"
                        />
                      </div>
                    </div>
                  </div>
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.nonRevenue">
                  <button @click="editNonRevenue" :disabled="selectedNonRevenueRows.length == 0"> Edit </button>

                  <button @click="deleteNonRevenue" :disabled="selectedNonRevenueRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveNonRevenue">
                    {{ formMode.nonRevenue == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelNonRevenue"> Cancel </button>
                </template>
              </div>
            </div>
          </div>

          <div v-if="isTMS" slot="Other Commission Settings" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Other Commission Settings"
                  :tableData="tmsFixedCommissions"
                  :selectedRow.sync="selectedTMSFixedRows"
                  :hideColumns="['SystemControl',]"
                />
              </div>
            </div>
            <div class="right-menu">
              <button :disabled="tableLoading || tmsFixedEditing || !selectedCommission" @click="editTMSFixedCommission(true)">New</button>
              <BoxContainer v-if="tmsFixedEditing" class="details-panel" :title="tmsFixedMode == 'new' ? 'New Fixed Commission' : 'Edit Fixed Commission'">
                <div class="field-grid one">
                  <Input placeholder="Fixed Commission Code" :inputValue.sync="tmsFixedForm.FixComCode" validator="text,10,uppercase,alphanumeric" :isDisabled="tmsFixedMode != 'new' || tmsFixedSaving" />
                  <Input placeholder="Description" :inputValue.sync="tmsFixedForm.FixComDesc" :isDisabled="String(tmsFixedForm.SystemControl) == '1' || tmsFixedSaving" />
                  <Dropdown
                    placeholder="Fixed Commission Group"
                    :items="tmsFixedGroupOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedTMSFixedGroup"
                    :isDisabled="String(tmsFixedForm.SystemControl) == '1' || tmsFixedSaving"
                  />
                  <Input placeholder="Driver Rate" :inputValue.sync="tmsFixedForm.FixComDriver" validator="positiveDecimal,19,5" :isDisabled="tmsFixedSaving" />
                  <Input placeholder="Attendant Rate" :inputValue.sync="tmsFixedForm.FixComAttendant" validator="positiveDecimal,19,5" :isDisabled="tmsFixedSaving" />
                </div>
              </BoxContainer>
              <div class="bottom-actions vertical">
                <template v-if="!tmsFixedEditing">
                  <button :disabled="tableLoading || !selectedTMSFixedRows.length" @click="editTMSFixedCommission(false)">Edit</button>
                  <button :disabled="tableLoading || !selectedTMSFixedRows.length || String(selectedTMSFixedRows[0].SystemControl) == '1'" @click="deleteTMSFixedCommission">Delete</button>
                </template>
                <template v-else>
                  <button :disabled="tmsFixedSaving" @click="saveTMSFixedCommission">{{ tmsFixedMode == 'new' ? 'Save New' : 'Save Edit' }}</button>
                  <button :disabled="tmsFixedSaving" @click="cancelTMSFixedCommission">Cancel</button>
                </template>
              </div>
            </div>
          </div>

          <div :slot="isTMS ? 'Area to Area Incentive' : 'Fixed-Zone Incentive'" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  :title="fixedZoneTitle"
                  :tableData="fixedZoneIncentives"
                  :selectedRow.sync="selectedFixedZoneRows"
                  :hideColumns="isTMS ? ['Driver Other Inc', 'Attendant Other Inc', 'B2B Coeff'] : []"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.fixedZone" @click="addRow('fixedZone')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.fixedZone"
                  class="details-panel"
                  :title="formMode.fixedZone == 'new' ? 'New Zone Details' : 'Edit Zone Details'"
                >
                  <div class="details-form">
                    <InputQuery
                      v-if="isHMS"
                      placeholder="Job Type"
                      :items="jobTypeOptions"
                      itemKey="Code - Description"
                      :selectedObject.sync="selectedFixedJobType"
                      :isDisabled="formMode.fixedZone == 'edit'"
                    />
                    <div class="zone-row">
                      <InputQuery
                        placeholder="From Zone"
                        :items="zoneList"
                        itemKey="Code - Description"
                        :selectedObject.sync="selectedFixedFromZone"
                        :isDisabled="formMode.fixedZone == 'edit'"
                      />
                    </div>
                    <div class="zone-row">
                      <InputQuery
                        placeholder="To Zone"
                        :items="zoneList"
                        itemKey="Code - Description"
                        :selectedObject.sync="selectedFixedToZone"
                        :isDisabled="formMode.fixedZone == 'edit'"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Driver</span>
                      <Input placeholder="$" :inputValue.sync="selectedFixedZone['Driver']" />
                      <span v-if="isHMS">+</span>
                      <Input v-if="isHMS" placeholder="%" :inputValue.sync="selectedFixedZone['Driver Other Inc']" />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Attendant</span>
                      <Input placeholder="$" :inputValue.sync="selectedFixedZone['Attendant']" />
                      <span v-if="isHMS">+</span>
                      <Input v-if="isHMS" placeholder="%" :inputValue.sync="selectedFixedZone['Attendant Other Inc']" />
                    </div>
                    <Input v-if="isHMS" placeholder="B2B Coeff" :inputValue.sync="selectedFixedZone['B2B Coeff']" />
                  </div>
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.fixedZone">
                  <button @click="editFixedZone" :disabled="selectedFixedZoneRows.length == 0"> Edit </button>

                  <button @click="deleteFixedZone" :disabled="selectedFixedZoneRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveFixedZone">
                    {{ formMode.fixedZone == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelFixedZone"> Cancel </button>
                </template>
              </div>
            </div>
          </div>

          <div slot="Add Incremental Incentive" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Add Incremental Incentive"
                  :tableData="incrementalIncentives"
                  :selectedRow.sync="selectedIncrementalRows"
                  :hideColumns="['IncrementType']"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.incremental" @click="addRow('incremental')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.incremental"
                  class="details-panel"
                  :title="formMode.incremental == 'new' ? 'New Increment Details' : 'Edit Increment Details'"
                >
                  <div class="details-form">
                    <Input
                      placeholder="Trip From"
                      :inputValue.sync="selectedIncremental['Trip From']"
                      validator="positiveInteger"
                      :isDisabled="formMode.incremental == 'edit'"
                    />
                    <div class="details-rate-row compact-rate">
                      <span>Driver</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Rate($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Rate(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Attendant</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Attendant Rate($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Attendant Rate(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Local</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Local($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Local(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Medium</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Medium($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Medium(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Outstn</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Outstation($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Outstation(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                  </div>
                  <RadioButtonSet
                    title="Type"
                    :buttons="incrementTypeOptions"
                    labelKey="label"
                    :selected.sync="selectedIncrementType"
                    :isHorizontal="true"
                    :showBox="false"
                    @radioChanged="incrementTypeChanged"
                  />
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.incremental">
                  <button @click="editIncremental" :disabled="selectedIncrementalRows.length == 0"> Edit </button>

                  <button @click="deleteIncremental" :disabled="selectedIncrementalRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveIncremental">
                    {{ formMode.incremental == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelIncremental"> Cancel </button>
                </template>
              </div>
            </div>
          </div>
        </Tab>
      </div>

      <div v-if="activePage == 'specialDay'" class="page-content split-view">
        <div class="list-side">
          <div class="content-header">
            <div>Special Day Incentive</div>
          </div>
          <div class="top-selector">
            <InputQuery
              placeholder="Commission Code"
              :items="commissionList"
              itemKey="Code - Description"
              :selectedObject.sync="selectedSpecialDayCommission"
              :isDisabled="editMode.specialDay"
              @selected="specialDayCommissionSelected"              
            />
          </div>
          <div class="tablebox-area">
            <TableBox
              v-if="!specialDayLoading"
              title="Special Day Incentive"
              :tableData="specialDayIncentives"
              :selectedRow.sync="selectedSpecialDayRows"
              :hideColumns="['Commission Code']"
            />
          </div>
        </div>

        <div class="right-menu">
          <button :disabled="editMode.specialDay || !selectedSpecialDayCommission" @click="newSpecialDay"> New </button>

          <transition name="details-expand">
            <BoxContainer
              v-if="editMode.specialDay"
              class="details-panel"
              :title="formMode.specialDay == 'new' ? 'New Special Day Details' : 'Edit Special Day Details'"
            >
              <div class="field-grid one">
                <Dropdown
                  placeholder="Occurrence Type"
                  :items="occurrenceOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedOccurrenceType"
                  :isDisabled="formMode.specialDay == 'edit'"
                />

                <DateTimePicker
                  v-if="
                    selectedOccurrenceType &&
                    (selectedOccurrenceType.value == 'A' || selectedOccurrenceType.value == 'O')
                  "
                  placeholder="Date"
                  title="Date"
                  :showTime="false"
                  :value.sync="selectedSpecialDayDate"
                  :maxDate="new Date('2079-01-01')"
                />

                <Dropdown
                  v-if="selectedOccurrenceType && selectedOccurrenceType.value == 'M'"
                  placeholder="Day of Month"
                  :items="specialDayOfMonthOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedSpecialDayOfMonth"
                />

                <Dropdown
                  v-if="selectedOccurrenceType && selectedOccurrenceType.value == 'W'"
                  placeholder="Day of Week"
                  :items="specialDayOfWeekOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedSpecialDayOfWeek"
                />

                <Dropdown
                  placeholder="Remark"
                  :items="dayRemarkOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedDayRemark"
                  :search.sync="specialDayRemarkText"
                  :allowFreeText="true"
                />

                <RadioButtonSet
                  class="special-day-movement-options"
                  title="Movement"
                  :buttons="visibleMovementScopeOptions"
                  labelKey="label"
                  :selected.sync="selectedMovementScope"
                  :isHorizontal="true"
                  :showBox="false"
                  :isDisabled="formMode.specialDay == 'edit'"
                />

                <div v-if="selectedMovementScope && selectedMovementScope.value == 0" class="special-day-movement-type">
                  <InputQuery
                    placeholder="Movement Type"
                    :items="specialMovementTypeOptions"
                    itemKey="Code - Description"
                    :selectedObject.sync="selectedSpecialMovementType"
                    :isDisabled="formMode.specialDay == 'edit'"
                  />
                </div>

                <Dropdown
                  placeholder="Incentive Type"
                  :items="incentiveTypeOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedIncentiveType"
                />

                <Input
                  :placeholder="
                    selectedIncentiveType ? `Incentive Value (${selectedIncentiveType.label})` : 'Incentive Value'
                  "
                  :inputValue.sync="selectedSpecialDay['Incentive Value']"
                  validator="positiveDecimal,19,2"
                />

                <Dropdown
                  v-if="isHMS && selectedIncentiveType && selectedIncentiveType.label == '%'"
                  placeholder="Base"
                  :items="baseOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedBase"
                />

                <div
                  v-if="selectedOccurrenceType && selectedOccurrenceType.value == 'M'"
                  class="special-day-monthly-note"
                >
                  Note: Special Incentive will not be incurred for months that do not have the specified day.
                </div>
              </div>
            </BoxContainer>
          </transition>

          <div class="bottom-actions vertical">
            <template v-if="!editMode.specialDay">
              <button :disabled="selectedSpecialDayRows.length == 0" @click="editSpecialDay"> Edit </button>

              <button :disabled="selectedSpecialDayRows.length == 0" @click="deleteSpecialDay"> Delete </button>
            </template>

            <template v-else>
              <button @click="saveSpecialDay">
                {{ formMode.specialDay == 'new' ? 'Save New' : 'Save Edit' }}
              </button>

              <button @click="cancelSpecialDay"> Cancel </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  computed: {
    isHMS: function () {
      return this.system == 'HMS';
    },
    isTMS: function () {
      return this.system == 'TMS';
    },
    fixedZoneTitle: function () {
      return this.isTMS ? 'Area to Area Incentive' : 'Fixed-Zone Incentive';
    },
    visibleMenu: function () {
      return this.menu.filter((item) => {
        return this.isHMS || item.title != 'Coefficient Settings';
      });
    },
    visibleDriverIncentiveTabs: function () {
      if (this.isTMS) {
        return [
          { title: 'Driver Commission Structure' },
          { title: 'Other Commission Settings' },
          { title: 'Area to Area Incentive' },
          { title: 'Add Incremental Incentive' },
        ];
      }

      return this.driverIncentiveTabs;
    },

    tmsRevenueRanges: function () {
      return (this.coefficientSettingsDetail.ComHeader?.ComDetails || [])
        .filter((row) => String(row.Com_Type).trim() == '0')
        .sort((a, b) => Number(a.Seq_No) - Number(b.Seq_No));
    },

    visibleMovementScopeOptions: function () {
      return this.movementScopeOptions.filter((item) => {
        return this.isHMS || item.value != 4;
      });
    },
  },
  data: function () {
    return {
      system: '',
      tmsFixedSaving: false,
      activePage: 'driverIncentive',
      driverTabIndex: 0,
      driverTabRenderKey: 0,
      manageMenuRenderKey: 0,
      selectedDriverCoeffMode: { label: 'No' },
      selectedNonRevenueRows: [],
      selectedFixedZoneRows: [],
      selectedIncrementalRows: [],
      selectedSpecialDayRows: [],
      editMode: {
        coefficientSettings: false,
        driverCommission: false,
        revenueMovement: false,
        specialDay: false,
        nonRevenue: false,
        fixedZone: false,
        incremental: false,
      },
      formMode: {
        driverCommission: '',
        revenueMovement: '',
        nonRevenue: '',
        fixedZone: '',
        incremental: '',
        specialDay: '',
      },
      menu: [
        {
          title: 'Coefficient Settings',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-todo-list-64.png',
          enabled: false,
          key: 'SSTab1',
        },
        {
          title: 'Driver Incentive',
          icon: 'https://dsmlib.logistics.systems/Icons/money.png',
          enabled: false,
          key: 'SSTab1_2',
        },
        {
          title: 'Special Day Incentive',
          icon: 'https://dsmlib.logistics.systems/Icons/Calendar.png',
          enabled: false,
          key: 'SSTab1_3',
        },
      ],
      driverIncentiveTabs: [
        { title: 'Driver Commission Structure' },
        { title: 'Revenue Movement Incentive' },
        { title: 'Non-Rev Mvt Inc (Fix)' },
        { title: 'Fixed-Zone Incentive' },
        { title: 'Add Incremental Incentive' },
      ],
      commissionList: [],
      driverCommissionList: [],
      jobTypeOptions: [],
      selectedFixedJobType: null,
      zoneList: [],
      selectedCommission: null,
      selectedDriverCommission: null,
      driverCommissionCode: '',
      driverCommissionDescription: '',
      selectedSpecialDayCommission: null,
      selectedNonRevenueTariff: null,
      selectedNonRevenueAttTariff: null,
      selectedIncrementType: { label: 'Gradual Increment', value: 0 },
      selectedOccurrenceType: { label: 'Annually', value: 'A' },
      selectedSpecialDayOfMonth: { label: '1', value: 1 },
      selectedSpecialDayOfWeek: { label: 'Sunday', value: 1 },
      selectedDayRemark: null,
      specialDayRemarkText: '',
      selectedSpecialMovementType: null,
      selectedMovementScope: { label: 'Select by Movement', value: 0 },
      selectedIncentiveType: { label: '%' },
      selectedBase: { label: 'of Driver Tariff Rate' },
      selectedSpecialDayDate: null,
      selectedMonthlyType: { label: '$' },
      selectedReserveType: { label: '%' },
      amountTypeOptions: [{ label: '$' }, { label: '%' }],
      tariffDropdown: [
        { value: 0, text: '-' },
        { value: 1, text: "of 'From' Tariff" },
        { value: 2, text: "of 'To' Tariff" },
        { value: 3, text: 'of Additional Billing' },
        { value: 4, text: 'of Driver Tariff Rate' },
        { value: 5, text: 'of Driver Fixed Zone Rate' },
      ],
      driverCoeffOptions: [{ label: 'No' }, { label: 'Yes' }, { label: '1 Way Special Additional' }],
      incrementTypeOptions: [
        { label: 'Gradual Increment', value: 0 },
        { label: 'Stage Increment', value: 1 },
      ],
      occurrenceOptions: [
        { label: 'Annually', value: 'A' },
        { label: 'Once', value: 'O' },
        { label: 'Monthly', value: 'M' },
        { label: 'Weekly', value: 'W' },
      ],
      specialDayOfMonthOptions: Array.from({ length: 31 }, (item, index) => ({
        label: String(index + 1),
        value: index + 1,
      })),
      specialDayOfWeekOptions: [
        { label: 'Sunday', value: 1 },
        { label: 'Monday', value: 2 },
        { label: 'Tuesday', value: 3 },
        { label: 'Wednesday', value: 4 },
        { label: 'Thursday', value: 5 },
        { label: 'Friday', value: 6 },
        { label: 'Saturday', value: 7 },
      ],
      dayRemarkOptions: [{ label: 'Labour Day' }, { label: 'Public Holiday' }, { label: 'Special Event' }],
      specialMovementTypeOptions: [],
      movementScopeOptions: [
        { label: 'Select by Movement', value: 0 },
        { label: 'All Movements', value: 1 },
        { label: 'All Rev Movements', value: 2 },
        { label: 'All non-Rev Movements', value: 3 },
        { label: 'All Fixed Zones', value: 4 },
      ],
      incentiveTypeOptions: [{ label: '%' }, { label: '$' }],
      baseOptions: [
        { label: 'of Driver Tariff Rate', value: 0 },
        { label: 'of Basic Incentive', value: 1 },
      ],
      movementTypes: ['DRD', 'D1W', 'C1W', 'DSS', 'DSE', 'CSS', 'CSE'],
      diversionTypes: ['DV1', 'DV2', 'DV3', 'DV4'],
      priceCoefficients: [],
      movementCoefficients: [],
      coefficientLoading: false,
      coefficientSettingsBackup: null,
      coefficientSettingsDetail: {},
      billChargeOptions: [],
      selectedUseBillAmtChg: [],
      B2BCoeff: '1.0000',
      driverCommissionBackup: null,
      tableLoading: false,
      revMvtIncList: [],
      revMvtIncDet: [],
      diversionDet: [],
      revenueMovementBackup: null,
      nonRevIncList: [],
      selectedNonRevenue: {},
      fixedZoneIncentives: [],
      selectedFixedZone: {},
      selectedFixedFromZone: null,
      selectedFixedToZone: null,
      incrementalIncentives: [],
      selectedIncremental: {},
      specialDayIncentives: [],
      selectedSpecialDay: {},
      specialDayLoading: false,

      tmsTripRateTypeOptions: [{ label: '%' }, { label: '$' }],
      tmsBasicPayTypeOptions: [
        { label: 'Fixed Monthly', value: 0 },
        { label: 'Per Attendance Day', value: 1 },
        { label: 'Fixed + Per Attendance Day', value: 2 },
      ],
      tmsFixedGroupOptions: [
        { label: 'LABOUR', value: 'LABOUR' },
        { label: 'OT', value: 'OT' },
        { label: 'OTHERS', value: 'OTHERS' },
      ],
      tmsFixedRateTypeOptions: [
        { label: '$ - Fixed Value', value: '$' },
        { label: '% - Percentage of Trip Commission', value: '%' },
      ],
      selectedTMSBasicPayType: null,
      selectedTMSRangeType: null,
      selectedTMSTripDriverType: null,
      selectedTMSTripAttendantType: null,
      selectedTMSFixedGroup: null,
      selectedTMSFixedDriverType: null,
      selectedTMSFixedAttendantType: null,
      tmsRangeTypes: [],
      tmsFixedCommissions: [],
      selectedTMSFixedRows: [],
      tmsFixedForm: {},
      tmsFixedMode: '',
      tmsFixedEditing: false,
    };
  },
  methods: {
    tmsRevenueRangeFrom: function (index) {
      if (index <= 0) return '0.00';
      const previous = this.tmsRevenueRanges[index - 1];
      const upper = Number(String(previous?.Range ?? '').replace(/,/g, ''));
      if (!Number.isFinite(upper) || upper <= 0) return '0.00';
      return ((Math.round(upper * 100) + 1) / 100).toFixed(2);
    },
    syncTMSCommissionDropdowns: function () {
      const header = this.coefficientSettingsDetail.ComHeader;

      if (!this.isTMS || !header) {
        this.selectedTMSBasicPayType = null;
        this.selectedTMSRangeType = null;
        this.selectedTMSTripDriverType = null;
        this.selectedTMSTripAttendantType = null;
        return;
      }

      this.selectedTMSBasicPayType =
        this.tmsBasicPayTypeOptions.find((item) => {
          return item.value == Number(header.CalculateBasicAttendance);
        }) || null;

      this.selectedTMSRangeType =
        this.tmsRangeTypes.find((item) => {
          return String(item.RangeType).trim() == String(header.RangeType ?? '').trim();
        }) || null;

      this.selectedTMSTripDriverType =
        this.tmsTripRateTypeOptions.find((item) => {
          return item.label == header.TripCommRateType1;
        }) || null;

      this.selectedTMSTripAttendantType =
        this.tmsTripRateTypeOptions.find((item) => {
          return item.label == header.TripCommRateType2;
        }) || null;
    },

    syncTMSFixedDropdowns: function () {
      this.selectedTMSFixedGroup =
        this.tmsFixedGroupOptions.find((item) => {
          return item.value == this.tmsFixedForm.FixComCodeGroup;
        }) || null;

      this.selectedTMSFixedDriverType =
        this.tmsFixedRateTypeOptions.find((item) => {
          return item.value == this.tmsFixedForm.FixComDriverQlf;
        }) || null;

      this.selectedTMSFixedAttendantType =
        this.tmsFixedRateTypeOptions.find((item) => {
          return item.value == this.tmsFixedForm.FixComAttendantQlf;
        }) || null;
    },

    validateTMSCommission: function () {
      const header = this.coefficientSettingsDetail.ComHeader;
      const fail = (message) => { this.$showAlert('warning', 'Invalid TMS Commission', message); return false; };
      if (!header) return fail('Please select or create a commission structure.');
      if (header.CalculateBasicAttendance == null || ![0, 1, 2].includes(Number(header.CalculateBasicAttendance))) {
        return fail('Please select a Basic Pay Type.');
      }
      if (!this.tmsRangeTypes.some((row) => String(row.RangeType) == String(header.RangeType))) return fail('Please select a valid Revenue Range Type.');
      for (const field of ['Basic_Pay1', 'Basic_Pay2', 'TripCommRate1', 'TripCommRate2']) {
        const value = Number(String(header[field] ?? '').replace(/,/g, ''));
        if (!Number.isFinite(value) || value < 0) return fail('Basic pay and trip rates must be zero or positive numbers.');
      }
      if (!['$', '%'].includes(header.TripCommRateType1) || !['$', '%'].includes(header.TripCommRateType2)) return fail('Please select valid trip rate types.');
      let previous = 0;
      for (const row of this.tmsRevenueRanges) {
        for (const field of ['Range', 'Driver', 'Attendant']) {
          const value = Number(String(row[field] ?? '').replace(/,/g, ''));
          if (!Number.isFinite(value) || value < 0) return fail('Revenue limits and percentages must be zero or positive numbers.');
        }
        const upper = Number(String(row.Range).replace(/,/g, ''));
        if (upper > 0 && upper <= previous) return fail('Non-zero revenue upper limits must increase.');
        if (upper > 0) previous = upper;
      }
      return true;
    },
    setTMSFixedCommissions: function (rows, selectedCode) {
      this.tmsFixedCommissions = rows.map((row) => ({
        ...row,
        FixComCode: String(row.FixComCode || '').trim(),
        FixComDesc: String(row.FixComDesc || '').trim(),
        FixComCodeGroup: String(row.FixComCodeGroup || 'OTHERS').trim().toUpperCase(),
        FixComDriver: row.FixComDriver ?? '0.00',
        FixComDriverQlf: String(row.FixComDriverQlf || '$').trim(),
        FixComAttendant: row.FixComAttendant ?? '0.00',
        FixComAttendantQlf: String(row.FixComAttendantQlf || '$').trim(),
        SystemControl: String(row.SystemControl || '0').trim(),
      }));
      const selected = this.tmsFixedCommissions.find((row) => row.FixComCode == selectedCode) || this.tmsFixedCommissions[0];
      this.selectedTMSFixedRows = selected ? [selected] : [];
    },
    loadTMSFixedCommissions: async function (selectedCode) {
      const path = this.$route.path;
      const code = this.getSelectedCommissionCode();
      const rows = await this.getManageList('GETTMSFIXEDCOMMISSIONS', { action: code });
      if (this.$route.path != path || this.getSelectedCommissionCode() != code || !this.isTMS) return;
      this.setTMSFixedCommissions(rows, selectedCode);
    },
    editTMSFixedCommission: function (isNew) {
      if (this.tmsFixedSaving || this.tableLoading || !this.getSelectedCommissionCode()) return;
      if (isNew) {
        this.tmsFixedForm = {
          FixComCode: '', FixComDesc: '', FixComCodeGroup: 'LABOUR',
          FixComDriver: '0.00', FixComDriverQlf: '$',
          FixComAttendant: '0.00', FixComAttendantQlf: '$', SystemControl: '0',
        };
      } else {
        if (!this.selectedTMSFixedRows.length) return;
        this.tmsFixedForm = JSON.parse(JSON.stringify(this.selectedTMSFixedRows[0]));
      }
      this.tmsFixedMode = isNew ? 'new' : 'edit';
      this.tmsFixedEditing = true;
      this.syncTMSFixedDropdowns();
    },
    cancelTMSFixedCommission: function () {
      if (this.tmsFixedSaving) return;
      this.tmsFixedEditing = false;
      this.tmsFixedMode = '';
      this.tmsFixedForm = {};
      this.syncTMSFixedDropdowns();
    },
    saveTMSFixedCommission: async function () {
      if (this.tmsFixedSaving || !this.isTMS) return;
      const row = this.tmsFixedForm;
      const code = String(row.FixComCode || '').trim().toUpperCase();
      const description = String(row.FixComDesc || '').trim();
      const commissionCode = this.getSelectedCommissionCode();
      if (!commissionCode || !code || !description) {
        this.$showAlert('warning', 'Required Fields', 'Please enter the fixed commission code and description.');
        return;
      }
      if (!['LABOUR', 'OT', 'OTHERS'].includes(row.FixComCodeGroup) ||
          !['$', '%'].includes(row.FixComDriverQlf) || !['$', '%'].includes(row.FixComAttendantQlf) ||
          ['FixComDriver', 'FixComAttendant'].some((field) => {
            const amount = Number(String(row[field] ?? '').replace(/,/g, ''));
            return !Number.isFinite(amount) || amount < 0;
          })) {
        this.$showAlert('warning', 'Invalid Fixed Commission', 'Check the group, rate types and amounts.');
        return;
      }
      if (this.tmsFixedMode == 'new' && this.tmsFixedCommissions.some((item) => item.FixComCode.toUpperCase() == code)) {
        this.$showAlert('warning', 'Duplicate Code', 'Select the existing fixed commission code and use Edit.');
        return;
      }
      this.tmsFixedSaving = true;
      this.$fullscreenLoading('Saving Fixed Commission');
      let saved = false;
      try {
        const operationRoutePath = this.$route.path;
        const operationSystem = this.system;
        const result = await this.manageApiCall('/Manage/SaveTMSFixedCommission', {
          action: this.tmsFixedMode == 'new' ? 'N' : 'E',
          comcode: commissionCode, fixcomcode: code, fixcomdesc: description,
          fixcomcodegroup: row.FixComCodeGroup,
          fixcomdriver: this.formatDecimalValue(row.FixComDriver, row.FixComDriverQlf == '%' ? 5 : 2),
          fixcomdriverqlf: row.FixComDriverQlf,
          fixcomattendant: this.formatDecimalValue(row.FixComAttendant, row.FixComAttendantQlf == '%' ? 5 : 2),
          fixcomattendantqlf: row.FixComAttendantQlf,
        });
        if (this.$route.path != operationRoutePath || this.system != operationSystem) return;
        if (String(result?.[0]?.Results) != '0') throw new Error(result?.[0]?.ErrMsg || 'Save was not confirmed.');
        saved = true;
        this.tmsFixedEditing = false;
        this.tmsFixedMode = '';
        this.tmsFixedForm = {};
        this.syncTMSFixedDropdowns();
        await this.loadTMSFixedCommissions(code);
        this.$showAlert('success', 'Saved', result[0].ErrMsg || 'Fixed Commission saved.');
      } catch (error) {
        this.$showAlert('error', saved ? 'Saved; Refresh Failed' : 'Save Failed', saved
          ? 'The save succeeded. Reload the commission list before making another change. ' + error.message
          : error.message || 'Unable to save Fixed Commission.');
      } finally {
        this.tmsFixedSaving = false;
        this.$fullscreenLoading();
      }
    },
    deleteTMSFixedCommission: async function () {
      if (this.tmsFixedSaving || !this.isTMS) return;
      const row = this.selectedTMSFixedRows[0];
      if (!row || String(row.SystemControl) == '1') return;
      const code = row.FixComCode;
      const commissionCode = this.getSelectedCommissionCode();
      const answer = await this.$showAlert(
        'warning', 'Delete Fixed Commission Code',
        'Deleting ' + code + ' removes this fixed code and its rates from ALL commission structures. Continue?',
        [{ text: 'Delete', color: 'red', textColor: 'white' }, { text: 'Cancel', color: 'lightgray', textColor: 'black' }],
      );
      if (answer != 'Delete' || this.tmsFixedSaving || this.getSelectedCommissionCode() != commissionCode || !this.isTMS) return;
      this.tmsFixedSaving = true;
      this.$fullscreenLoading('Deleting Fixed Commission');
      let deleted = false;
      try {
        const operationRoutePath = this.$route.path;
        const operationSystem = this.system;
        const result = await this.manageApiCall('/Manage/SaveTMSFixedCommission', {
          action: 'D', comcode: commissionCode, fixcomcode: code,
        });
        if (this.$route.path != operationRoutePath || this.system != operationSystem) return;
        if (String(result?.[0]?.Results) != '0') throw new Error(result?.[0]?.ErrMsg || 'Deletion was not confirmed.');
        deleted = true;
        await this.loadTMSFixedCommissions();
        this.$showAlert('success', 'Deleted', result[0].ErrMsg || 'Fixed Commission deleted.');
      } catch (error) {
        this.$showAlert('error', deleted ? 'Deleted; Refresh Failed' : 'Delete Failed', deleted
          ? 'Deletion succeeded. Reload the commission list. ' + error.message
          : error.message || 'Unable to delete Fixed Commission.');
      } finally {
        this.tmsFixedSaving = false;
        this.$fullscreenLoading();
      }
    },
    canChangeManageTab: function () {
      if (Object.values(this.editMode).some((editing) => editing) || this.tmsFixedEditing || this.tmsFixedSaving) {
        this.$showAlert(
          'warning',
          'Unsaved Changes',
          'Please save or cancel the current creation or edit before changing tabs.',
        );
        return false;
      }

      return true;
    },

    driverTabChanged: function (index) {
      if (index == this.driverTabIndex) return;
      if (!this.canChangeManageTab()) {
        this.driverTabRenderKey++;
        return;
      }
      this.driverTabIndex = index;
    },

    menuClicked: function (item) {
      const pages = {
        'Coefficient Settings': 'coefficientSettings',
        'Driver Incentive': 'driverIncentive',
        'Special Day Incentive': 'specialDay',
      };
      if (!pages[item.title] || pages[item.title] == this.activePage) return;
      if (!this.canChangeManageTab()) {
        this.manageMenuRenderKey++;
        return;
      }

      switch (item.title) {
        case 'Coefficient Settings':
          this.activePage = 'coefficientSettings';
          break;

        case 'Driver Incentive':
          this.activePage = 'driverIncentive';
          break;

        case 'Special Day Incentive':
          this.activePage = 'specialDay';
          break;
      }
    },

    //#region coefficient settings
    mapPriceCoefficients: function (details) {
      const jobTypes = [
        { code: 'IMP', label: 'IMPORT' },
        { code: 'EXP', label: 'EXPORT' },
        { code: '1-IMP', label: '1-WAY IMPORT' },
        { code: '1-EXP', label: '1-WAY EXPORT' },
        { code: 'IMP-EXP', label: 'IMPORT-EXPORT' },
        { code: 'MTR', label: 'EMPTY-REJECT' },
        { code: 'REPO', label: 'REPOSITION' },
        { code: '1-IMP-MT', label: '1-WAY IMP (MT)' },
        { code: '1-EXP-MT', label: '1-WAY EXP (MT)' },
      ];

      return jobTypes.map((jobType, index) => {
        const data = (details || []).find((item) => {
          const code = String(item.JobTypeCode ?? item.JobType ?? item['Job Type'] ?? '').trim();

          return code == jobType.code;
        });

        return {
          '#': index + 1,
          JobTypeCode: jobType.code,
          'Job Type': jobType.label,
          'Price Coefficient': this.formatDecimalValue(
            data?.PriceCoefficient ?? data?.['Price Coefficient'] ?? data?.JobTypeCoefficient ?? '0',
            5,
          ),
        };
      });
    },
    mapMovementCoefficients: function (details) {
      const jobTypes = [
        { code: 'IMP', label: 'IMPORT' },
        { code: 'EXP', label: 'EXPORT' },
        { code: '1-IMP', label: '1-WAY IMPORT' },
        { code: '1-EXP', label: '1-WAY EXPORT' },
        { code: 'IMP-EXP', label: 'IMPORT-EXPORT' },
        { code: 'MTR', label: 'EMPTY-REJECT' },
        { code: 'REPO', label: 'REPOSITION' },
        { code: '1-IMP-MT', label: '1-WAY IMP (MT)' },
        { code: '1-EXP-MT', label: '1-WAY EXP (MT)' },
      ];

      const disabledCombinations = [
        {
          JobType: '1-IMP',
          MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'],
        },
        {
          JobType: '1-EXP',
          MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'],
        },
        {
          JobType: 'REPO',
          MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'],
        },
        {
          JobType: '1-IMP-MT',
          MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'],
        },
        {
          JobType: '1-EXP-MT',
          MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'],
        },
      ];

      const isDisabled = function (jobType, movementType) {
        return disabledCombinations.some((item) => {
          return item.JobType == jobType && item.MovementTypes.includes(movementType);
        });
      };

      return jobTypes.map((jobType) => {
        const row = {
          JobTypeCode: jobType.code,
          'Job Type': jobType.label,
        };

        this.movementTypes.forEach((movementType) => {
          const data = (details || []).find((item) => {
            const combinedKey = String(item.JTypeMvt ?? item.ControlTag ?? '').trim();
            const itemMovementType = String(item.MovementType ?? item['Movement Type'] ?? '').trim();
            const itemJobType = String(item.JobTypeCode ?? item.JobType ?? item['Job Type'] ?? '').trim();

            return (
              (itemJobType == jobType.code && itemMovementType == movementType) ||
              combinedKey == `${jobType.code}${movementType}`
            );
          });

          const disabled = isDisabled(jobType.code, movementType);

          row[movementType] = {
            value:
              disabled ? '' : (
                this.formatDecimalValue(
                  data?.MvtRateCoefficient ??
                    data?.MovementTypeCoefficient ??
                    data?.['Movement Type Coefficient'] ??
                    data?.Coefficient ??
                    data?.Coeff ??
                    data?.Value ??
                    '0',
                  5,
                )
              ),

            disabled: disabled,
            JobType: jobType.code,
            MovementType: movementType,
            key: data?.JTypeMvt || `${jobType.code}${movementType}`,
          };
        });

        return row;
      });
    },
    formatPriceCoefficient: function (row) {
      row['Price Coefficient'] = this.formatDecimalValue(row['Price Coefficient'], 5);
    },
    formatCoefficientCell: function (cell) {
      if (!cell || cell.disabled) {
        return;
      }

      cell.value = this.formatDecimalValue(cell.value, 5);
    },
    editCoefficientSettings: function () {
      this.coefficientSettingsBackup = {
        priceCoefficients: JSON.parse(JSON.stringify(this.priceCoefficients || [])),
        movementCoefficients: JSON.parse(JSON.stringify(this.movementCoefficients || [])),
        selectedDriverCoeffMode: JSON.parse(JSON.stringify(this.selectedDriverCoeffMode || {
              label: 'No',
            },
          ),
        ),
      };

      this.editMode.coefficientSettings = true;
    },

    cancelCoefficientSettings: function () {
      if (this.coefficientSettingsBackup) {
        this.priceCoefficients = JSON.parse(JSON.stringify(this.coefficientSettingsBackup.priceCoefficients || []));
        this.movementCoefficients = JSON.parse(JSON.stringify(this.coefficientSettingsBackup.movementCoefficients || []));

        this.selectedDriverCoeffMode = JSON.parse(JSON.stringify( this.coefficientSettingsBackup.selectedDriverCoeffMode || {
              label: 'No',
            },
          ),
        );
      }

      this.editMode.coefficientSettings = false;
      this.coefficientSettingsBackup = null;
    },

    buildMovementCoefficientPayload: function () {
      const payload = [];

      this.movementCoefficients.forEach((row) => { this.movementTypes.forEach((movementType) => {
        const cell = row[movementType];

        if (!cell || cell.disabled) {
          return;
        }

        payload.push({
          JobType: row.JobTypeCode,
          MovementType: movementType,
          JTypeMvt: cell.key || `${row.JobTypeCode}${movementType}`,
          Coefficient: this.formatDecimalValue(cell.value, 5),
        });
        });
      });

      return payload;
    },

    saveCoefficientSettings: async function () {
      const movementCoefficients = this.buildMovementCoefficientPayload();

      const priceCoefficients = this.priceCoefficients.map((item) => {
        return {
          jobtype: String(item.JobTypeCode || ''),
          value: this.formatDecimalValue(item['Price Coefficient'], 5),
        };
      });

      let driverCoeffMode = '0';

      if (this.selectedDriverCoeffMode?.label == 'Yes') {
        driverCoeffMode = '1';
      } else if (this.selectedDriverCoeffMode?.label == '1 Way Special Additional') {
        driverCoeffMode = '2';
      }

      const requestBody = {
        drivercoeffmode: driverCoeffMode,
        pricecoefficients: priceCoefficients,
        movementcoefficients: movementCoefficients.map((item) => {
          return {
            jobtype: String(item.JobType || ''),
            movementtype: String(item.MovementType || ''),
            key: String(item.JTypeMvt || ''),
            value: this.formatDecimalValue(item.Coefficient, 5),
          };
        }),
      };

      this.$fullscreenLoading('Updating Coefficient Settings');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveCoefficientSettings', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);

        return;
      }

      this.editMode.coefficientSettings = false;
      this.coefficientSettingsBackup = null;

      this.$showAlert('success', 'Updated', result?.[0]?.['ErrMsg'] || 'Coefficient Settings updated successfully.');
    },

    //#region special day inc
    specialDayCommissionSelected: async function (selected) {
      if (!selected) {
        return;
      }

      const comCode = String(selected['Com Code'] || '').trim();

      if (!comCode) {
        return;
      }

      const normalCommission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (!normalCommission) {
        return;
      }

      this.selectedSpecialDayCommission = normalCommission;

      await this.loadSpecialDayDetails(normalCommission);
    },
    loadSpecialDayDetails: async function (selected, throwOnError = false) {
      if (!selected) return;
      const code = String(selected['Com Code'] || '').trim();
      const path = this.$route.path;
      const loadId = (this._specialDayLoadId || 0) + 1;
      this._specialDayLoadId = loadId;
      this.editMode.specialDay = false;
      this.formMode.specialDay = '';
      this.specialDayLoading = true;
      this.specialDayIncentives = [];
      this.selectedSpecialDayRows = [];
      this.$fullscreenLoading('Populating List');
      try {
        const rows = await this.getManageList('GETSPECIALDAYINC', { action: code });
        if (this._specialDayLoadId != loadId || this.$route.path != path) return;
        this.specialDayIncentives = rows;
        this.selectedSpecialDayRows = rows.length ? [rows[0]] : [];
        this.syncSelectedRows();
      } catch (error) {
        if (this._specialDayLoadId != loadId || this.$route.path != path) return;
        if (throwOnError) throw error;
        this.$showAlert('error', 'Load Failed', error.message || 'Unable to load Special Day Incentives.');
      } finally {
        if (this._specialDayLoadId == loadId) {
          this.specialDayLoading = false;
          this.$fullscreenLoading();
        }
      }
    },
    syncSpecialDayDetails: function () {
      const specialDay = this.selectedSpecialDay || {};
      const occurrenceCode = String(specialDay['Occur'] || '').trim();

      this.selectedOccurrenceType = this.occurrenceOptions.find((item) => {
        return item.value == occurrenceCode || item.label == occurrenceCode;
      }) || null;

      const remark = String(specialDay['Day remarks'] || '').trim();

      this.selectedDayRemark = this.dayRemarkOptions.find((item) => {
        return item.label == remark;
      }) || (remark ? { label: remark } : null);
      this.specialDayRemarkText = remark;

      const movementType = String(specialDay['MovementType'] || specialDay['Mvt Type'] || '').trim();
      const movementOption = Number(specialDay['Movement Option']);
      const isFixedZone = movementType.toUpperCase() == '@F@' || movementType.toUpperCase() == 'ALL FIXED ZONES';
      const movementScopeValue = isFixedZone ? 4 : [1, 2, 3].includes(movementOption) ? movementOption : 0;

      this.selectedMovementScope = this.movementScopeOptions.find((item) => {
        return item.value == movementScopeValue;
      }) || this.movementScopeOptions[0];
      this.selectedSpecialMovementType = isFixedZone ? null : (this.specialMovementTypeOptions.find((item) => {
          return String(item.MovementType || '').trim() == movementType;
        }) ||
        (movementType ?
          {
            MovementType: movementType,
            MovementTypeDesc: '',
            'Movement Type - Description': movementType,
          }
        : null)
      );

      const incentiveType = String(specialDay['Type'] || '').trim();

      this.selectedIncentiveType = this.incentiveTypeOptions.find((item) => {
        return item.label == incentiveType;
      }) || (incentiveType ? { label: incentiveType } : null);

      const base = String(specialDay['Base'] || '').trim();

      this.selectedBase = this.baseOptions.find((item) => {
        return item.label == base || String(item.value) == base;
      }) || (base ? { label: base } : null);

      const day = String(specialDay['Day'] || '').trim();
      if (day && (occurrenceCode == 'A' || occurrenceCode == 'O')) {
        this.selectedSpecialDayDate = new Date(day);
      } else if (occurrenceCode == 'M') {
        this.selectedSpecialDayOfMonth =
          this.specialDayOfMonthOptions.find((item) => {
            return String(item.value) == day;
          }) || this.specialDayOfMonthOptions[0];
      } else if (occurrenceCode == 'W') {
        this.selectedSpecialDayOfWeek =
          this.specialDayOfWeekOptions.find((item) => {
            return String(item.value) == day;
          }) || this.specialDayOfWeekOptions[0];
      }
    },
    newSpecialDay: function () {
      const comCode = String(this.selectedSpecialDayCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');

        return;
      }

      this.selectedSpecialDay = {
        ID: '',
        'Commission Code': comCode,
        Occur: 'A',
        Day: '',
        'Day remarks': '',
        'Mvt Type': '',
        'Movement Scope': 'Select by Movement',
        'Movement Option': 0,
        'Incentive Value': '0.00',
        Type: '%',
        Base: 'of Driver Tariff Rate',
      };

      this.selectedOccurrenceType = {
        label: 'Annually',
        value: 'A',
      };
      this.selectedSpecialDayDate = new Date();
      this.selectedSpecialDayOfMonth = this.specialDayOfMonthOptions[0];
      this.selectedSpecialDayOfWeek = this.specialDayOfWeekOptions[0];
      this.selectedDayRemark = null;
      this.specialDayRemarkText = '';
      this.selectedSpecialMovementType = null;
      this.selectedMovementScope = this.movementScopeOptions.find((item) => {
        return item.label == 'Select by Movement';
      }) || null;

      this.selectedIncentiveType = this.incentiveTypeOptions.find((item) => {
        return item.label == (this.isTMS ? '$' : '%');
      }) || null;

      this.selectedBase = this.baseOptions.find((item) => {
        return item.label == 'of Driver Tariff Rate';
      }) || null;

      this.formMode.specialDay = 'new';
      this.editMode.specialDay = true;
    },
    editSpecialDay: function () {
      if (!this.selectedSpecialDayRows || this.selectedSpecialDayRows.length == 0) {
        return;
      }

      this.selectedSpecialDay = JSON.parse(JSON.stringify(this.selectedSpecialDayRows[0]));

      this.syncSpecialDayDetails();

      this.formMode.specialDay = 'edit';
      this.editMode.specialDay = true;
    },
    cancelSpecialDay: function () {
      this.editMode.specialDay = false;
      this.formMode.specialDay = '';
      this.syncSelectedRows();
    },
    saveSpecialDay: async function () {
      const mode = this.formMode.specialDay;
      const comCode = String(this.selectedSpecialDayCommission?.['Com Code'] || '').trim();
      const occurrenceType = String(this.selectedOccurrenceType?.value || '').trim();
      const movementOption = Number(this.selectedMovementScope?.value ?? 0);
      const movementType = movementOption == 4 ? '@F@' : String(this.selectedSpecialMovementType?.MovementType || '').trim();
      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }
      if (!occurrenceType) {
        this.$showAlert('warning', 'Occurrence Type Required', 'Please select an Occurrence Type.');
        return;
      }
      let day = String(this.selectedSpecialDay['Day'] || '').trim();
      if (occurrenceType == 'A' || occurrenceType == 'O') {
        day = this.selectedSpecialDayDate ? new Date(this.selectedSpecialDayDate).formatString('yyyy-MM-dd') : '';
      } else if (occurrenceType == 'M') {
        day = String(this.selectedSpecialDayOfMonth?.value || '');
      } else if (occurrenceType == 'W') {
        day = String(this.selectedSpecialDayOfWeek?.value || '');
      }
      if (!day) {
        this.$showAlert('warning', 'Day Required', 'Please select a date or day.');
        return;
      }
      if (this.isTMS && movementOption == 4) {
        this.$showAlert('warning', 'Invalid Movement Scope', 'All Fixed Zones is available for HMS only.');
        return;
      }
      if (movementOption == 0 && !movementType) {
        this.$showAlert('warning', 'Movement Type Required', 'Please select a Movement Type.');
        return;
      }
      if (!this.selectedIncentiveType) {
        this.$showAlert('warning', 'Incentive Type Required', 'Please select an Incentive Type.');
        return;
      }
      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        id: String(this.selectedSpecialDay['ID'] || this.selectedSpecialDay['SpecialDayRateID'] || '').trim(),
        comcode: comCode,
        occur: occurrenceType,
        day: day,
        remark: String(this.specialDayRemarkText || '').trim(),
        movementoption: String(movementOption),
        movementtype: movementType,
        incentivetype: String(this.selectedIncentiveType.label || '').trim(),
        incentivevalue: this.formatDecimalValue(this.selectedSpecialDay['Incentive Value'], 2),
        ...(this.isHMS ? { incentivebase: String(this.selectedBase?.value ?? 0) } : {}),
      };
      this.$fullscreenLoading(mode == 'new' ? 'Creating Special Day Incentive' : 'Updating Special Day Incentive');
      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveSpecialDayInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;
      this.$fullscreenLoading();
      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }
      const remarks = await this.getManageList('GETSPECIALDAY');
      this.dayRemarkOptions = (remarks || []).map((item) => ({
        ...item,
        label: item.DateRemark,
      }));
      await this.loadSpecialDayDetails(this.selectedSpecialDayCommission);
      this.$showAlert(
        'success',
        mode == 'new' ? 'Created' : 'Updated',
        result?.[0]?.['ErrMsg'] ||
          (mode == 'new' ?
            'Special Day Incentive created successfully.'
          : 'Special Day Incentive updated successfully.'),
      );
    },
    deleteSpecialDay: async function () {
      if (!this.selectedSpecialDayRows || this.selectedSpecialDayRows.length == 0) {
        return;
      }
      const selectedRow = this.selectedSpecialDayRows[0];
      const id = String(selectedRow['ID'] || selectedRow['SpecialDayRateID'] || '').trim();
      if (!id) {
        this.$showAlert('warning', 'Delete Failed', 'Special Day Rate ID is missing.');
        return;
      }
      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Special Day Incentive',
        `Are you sure you want to delete Special Day Rate <b>${id}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );
      if (confirmDelete == 'Cancel') {
        return;
      }
      this.$fullscreenLoading('Deleting Special Day Incentive');
      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveSpecialDayInc', { action: 'D', id: id });
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;
      this.$fullscreenLoading();
      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }
      await this.loadSpecialDayDetails(this.selectedSpecialDayCommission);
      this.$showAlert('success', 'Deleted', result?.[0]?.['ErrMsg'] || 'Special Day Incentive deleted successfully.');
    },
    //#endregion

    //#region driver incentive
    newDriverCommission: function () {
      this.driverCommissionBackup = {
        detail: JSON.parse(JSON.stringify(this.coefficientSettingsDetail || {})),
        selectedCommissionCode:
          this.selectedDriverCommission?.['Com Code'] || this.selectedCommission?.['Com Code'] || '',
        code: this.driverCommissionCode,
        description: this.driverCommissionDescription,
        useBillAmtChg: [...this.selectedUseBillAmtChg],
        b2bCoeff: this.B2BCoeff,
      };

      this.formMode.driverCommission = 'new';
      this.editMode.driverCommission = true;

      this.selectedDriverCommission = null;
      this.driverCommissionCode = '';
      this.driverCommissionDescription = '';

      this.selectedUseBillAmtChg = [];
      this.B2BCoeff = '1.0000';

      const comHeader = {
        Com_Code: '',
        Remark: '',
        Basic_Pay1: '0.00',
        Basic_Pay2: '0.00',
        ComDetails: [],
      };

      this.coefficientSettingsDetail = this.mapCommissionStructure({
        ComHeader: comHeader,
      });

      this.coefficientSettingsDetail.monthlyBonus.Qlf = '$';
      this.coefficientSettingsDetail.reserve.Qlf = '%';

      this.selectedMonthlyType = { label: '$' };
      this.selectedReserveType = { label: '%' };
      this.syncTMSCommissionDropdowns();
    },
    editDriverCommission: function () {
      if (!this.coefficientSettingsDetail.ComHeader) {
        return;
      }

      this.driverCommissionBackup = {
        detail: JSON.parse(JSON.stringify(this.coefficientSettingsDetail)),
        selectedCommissionCode: this.selectedCommission?.['Com Code'] || this.selectedDriverCommission?.['Com Code'] || '',
        code: this.driverCommissionCode,
        description: this.driverCommissionDescription,
        useBillAmtChg: [...this.selectedUseBillAmtChg],
        b2bCoeff: this.B2BCoeff,
      };

      this.formMode.driverCommission = 'edit';
      this.editMode.driverCommission = true;
    },
    cancelDriverCommission: function () {
      if (!this.driverCommissionBackup) {
        return;
      }

      const backup = this.driverCommissionBackup;

      this.coefficientSettingsDetail = JSON.parse(JSON.stringify(backup.detail || {}));
      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';
      this.selectedUseBillAmtChg = [...(backup.useBillAmtChg || [])];
      this.B2BCoeff = backup.b2bCoeff || '1.0000';

      this.syncCommissionSelectors(backup.selectedCommissionCode);

      this.driverCommissionCode = backup.code || backup.selectedCommissionCode || '';
      this.driverCommissionDescription = backup.description || '';

      if (this.coefficientSettingsDetail.monthlyBonus) {
        this.selectedMonthlyType = {
          label: this.coefficientSettingsDetail.monthlyBonus.Qlf || '$',
        };
      }

      if (this.coefficientSettingsDetail.reserve) {
        this.selectedReserveType = {
          label: this.coefficientSettingsDetail.reserve.Qlf || '%',
        };
      }

      this.syncTMSCommissionDropdowns();
      this.driverCommissionBackup = null;
    },
    saveDriverCommission: async function () {
      const mode = this.formMode.driverCommission;
      const commissionCode = String(this.driverCommissionCode || '').trim();
      const description = String(this.driverCommissionDescription || '').trim();

      if (!commissionCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please enter a Commission Code.');
        return;
      }

      if (mode == 'new') {
        const duplicate = this.commissionList.some((item) => {
          return (
            String(item['Com Code'] || '').trim().toUpperCase() == commissionCode.toUpperCase()
          );
        });

        if (duplicate) {
          this.$showAlert('warning', 'Duplicate Commission Code', `Commission Code ${commissionCode} already exists.`);
          return;
        }
      }

      if (!description) {
        this.$showAlert('warning', 'Description Required', 'Please enter a Description.');
        return;
      }

      if (this.isTMS && !this.validateTMSCommission()) return;
      const commission = this.buildCommissionPayload();

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: commissionCode,
        remark: description,
        rangetype: String(commission.ComHeader.RangeType ?? (this.isTMS ? '' : 0)),
        basicpay1: commission.ComHeader.Basic_Pay1,
        basicpay2: commission.ComHeader.Basic_Pay2,
        ...(this.isHMS ? {
          usebillamtchg: this.selectedUseBillAmtChg
            .map((item) => String(item.ChgCode || '').trim().toUpperCase())
            .filter(Boolean)
            .join(','),
          b2bcoeff: this.B2BCoeff || '1.0000',
        } : {
          tripcommrate1: this.formatDecimalValue(
            commission.ComHeader.TripCommRate1,
            commission.ComHeader.TripCommRateType1 == '%' ? 5 : 2,
          ),
          tripcommratetype1: commission.ComHeader.TripCommRateType1,
          tripcommrate2: this.formatDecimalValue(
            commission.ComHeader.TripCommRate2,
            commission.ComHeader.TripCommRateType2 == '%' ? 5 : 2,
          ),
          tripcommratetype2: commission.ComHeader.TripCommRateType2,
          calculatebasicattendance: String(
            commission.ComHeader.CalculateBasicAttendance,
          ),
        }),
        details: (commission.ComHeader.ComDetails || []).map((detail) => {
          return {
            comtype: String(detail.Com_Type ?? '').trim(),
            seqno: String(detail.Seq_No ?? ''),
            range: detail.Range,
            incevery: detail.IncEvery,
            qlf: String(detail.Qlf || '$'),
            remarks: String(detail.Remarks || detail.Remark || ''),
            driver: detail.Driver,
            attendant: detail.Attendant,
          };
        }),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Commission Structure' : 'Updating Commission Structure');
      let saved = false;
      try {
        const operationRoutePath = this.$route.path;
        const operationSystem = this.system;
        const result = await this.manageApiCall('/Manage/SaveDrvCom', requestBody);
        if (this.$route.path != operationRoutePath || this.system != operationSystem) return;
        if (String(result?.[0]?.Results) != '0') throw new Error(result?.[0]?.ErrMsg || 'Save was not confirmed.');
        saved = true;
        this.editMode.driverCommission = false;
        this.formMode.driverCommission = '';
        this.driverCommissionBackup = null;
        await this.refreshCommissionList(commissionCode);
        this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0].ErrMsg || 'Commission Structure saved.');
      } catch (error) {
        this.$showAlert('error', saved ? 'Saved; Refresh Failed' : 'Save Failed', saved
          ? 'The save succeeded. Reload the commission list before making another change. ' + error.message
          : error.message || 'Unable to save Commission Structure.');
      } finally {
        this.$fullscreenLoading();
      }
    },

    deleteDriverCommission: async function () {
      if (!this.coefficientSettingsDetail.ComHeader) {
        return;
      }

      const comCode = this.getSelectedCommissionCode();

      if (!comCode) {
        return;
      }

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Commission Structure',
        `Are you sure you want to delete Commission Code <b>${comCode}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: comCode,
        remark: '',
        rangetype: '',
        basicpay1: '0.00',
        basicpay2: '0.00',
        details: [],
      };

      this.$fullscreenLoading('Deleting Commission Structure');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveDrvCom', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      await this.refreshCommissionList('');

      this.$showAlert(
        'success',
        'Commission Deleted',
        result?.[0]?.['ErrMsg'] || `Commission ${comCode} deleted successfully.`,
      );
    },

    editRevenueMovement: function () {
      if (!this.selectedCommission) {
        return;
      }

      this.revenueMovementBackup = {
        revMvtIncList: JSON.parse(JSON.stringify(this.revMvtIncList || [])),
        revMvtIncDet: JSON.parse(JSON.stringify(this.revMvtIncDet || [])),
        diversionDet: JSON.parse(JSON.stringify(this.diversionDet || [])),
      };

      this.formMode.revenueMovement = 'edit';
      this.editMode.revenueMovement = true;
    },

    cancelRevenueMovement: function () {
      if (this.revenueMovementBackup) {
        this.revMvtIncList = JSON.parse(JSON.stringify(this.revenueMovementBackup.revMvtIncList || []));
        this.revMvtIncDet = JSON.parse(JSON.stringify(this.revenueMovementBackup.revMvtIncDet || []));
        this.diversionDet = JSON.parse(JSON.stringify(this.revenueMovementBackup.diversionDet || []));
      }

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;
    },

    saveRevenueMovement: async function () {
      const comCode = this.getSelectedCommissionCode();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      const incentives = this.buildRevenueMovementPayload();
      const requestBody = {
        comcode: comCode,

        incentives: incentives.map((item) => {
          return {
            jobtype: String(item.JobType || ''),
            movementtype: String(item.MovementType || ''),
            key: String(item.JTypeMvt || ''),
            type: String(item.IncentiveType || '$'),
            value: String(item.IncentiveValue || '0'),
          };
        }),
      };

      this.$fullscreenLoading('Updating Revenue Movement Incentive');
      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveRevMvtInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;
      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;

      const commission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert(
        'success',
        'Updated',
        result?.[0]?.['ErrMsg'] || 'Revenue Movement Incentive updated successfully.',
      );
    },
    addRow: function (section) {
      this.formMode[section] = 'new';

      if (section == 'nonRevenue') {
        this.selectedNonRevenue = {};
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
      }

      if (section == 'fixedZone') {
        this.selectedFixedZone = {};
        this.selectedFixedJobType = null;
        this.selectedFixedFromZone = null;
        this.selectedFixedToZone = null;
      }

      if (section == 'incremental') {
        this.selectedIncremental = {};
        this.selectedIncrementType = { label: 'Gradual Increment', value: 0 };
      }

      this.editMode[section] = true;
    },
    editNonRevenue: function () {
      if (!this.selectedNonRevenueRows || this.selectedNonRevenueRows.length == 0) {
        return;
      }

      this.selectedNonRevenue = JSON.parse(JSON.stringify(this.selectedNonRevenueRows[0]));

      this.selectedNonRevenueTariff = this.tariffDropdown.find((item) => {
        return item.value == this.selectedNonRevenue['IncentivePercentType'];
      }) || null;

      this.selectedNonRevenueAttTariff = this.tariffDropdown.find((item) => {
        return item.value == this.selectedNonRevenue['AttnIncentivePercentType'];
      }) || null;

      this.formMode.nonRevenue = 'edit';
      this.editMode.nonRevenue = true;
    },
    cancelNonRevenue: function () {
      this.editMode.nonRevenue = false;
      this.formMode.nonRevenue = '';

      this.syncSelectedRows();

      const selectedRow = this.selectedNonRevenueRows[0];

      if (!selectedRow) {
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
        return;
      }

      this.selectedNonRevenueTariff = this.tariffDropdown.find((item) => {
        return item.value == selectedRow['IncentivePercentType'];
      }) || null;

      this.selectedNonRevenueAttTariff = this.tariffDropdown.find((item) => {
        return item.value == selectedRow['AttnIncentivePercentType'];
      }) || null;
    },

    saveNonRevenue: async function () {
      const mode = this.formMode.nonRevenue;
      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      const movementType = String(this.selectedNonRevenue['Movement'] || '').trim();

      if (!movementType) {
        this.$showAlert('warning', 'Movement Type Required', 'Please enter a Movement Type.');
        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        movementtype: movementType,
        movementtypedesc: String(this.selectedNonRevenue['Description'] || '').trim(),
        driverfix: this.selectedNonRevenue['Driver Rate($)'],
        driverpercent: this.selectedNonRevenue['Driver Rate(%)'],
        attnfix: this.selectedNonRevenue['Attendant Rate($)'],
        attnpercent: this.selectedNonRevenue['Attendant Rate(%)'],
        driverpercenttype: String(this.selectedNonRevenueTariff?.value ?? 0),
        attnpercenttype: String(this.selectedNonRevenueAttTariff?.value ?? 0),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Non-Revenue Incentive' : 'Updating Non-Revenue Incentive');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveNonRevInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.nonRevenue = false;
      this.formMode.nonRevenue = '';

      await this.refreshCommissionDetails(comCode);

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteNonRevenue: async function () {
      if (!this.selectedNonRevenueRows || this.selectedNonRevenueRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedNonRevenueRows[0];
      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();
      const movementType = String(selectedRow['Movement'] || '').trim();

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Non-Revenue Incentive',
        `Are you sure you want to delete Movement Type <b>${movementType}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: comCode,
        movementtype: movementType,
        movementtypedesc: '',
        driverfix: '0',
        driverpercent: '0',
        attnfix: '0',
        attnpercent: '0',
        driverpercenttype: '0',
        attnpercenttype: '0',
      };

      this.$fullscreenLoading('Deleting Non-Revenue Incentive');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveNonRevInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      await this.refreshCommissionDetails(comCode);

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    editFixedZone: function () {
      if (!this.selectedFixedZoneRows || this.selectedFixedZoneRows.length == 0) {
        return;
      }

      this.selectedFixedZone = JSON.parse(JSON.stringify(this.selectedFixedZoneRows[0]));

      this.selectedFixedJobType = this.jobTypeOptions.find((item) => {
        const jobType = item.JobType || item['Job Type'];
        const selectedJobType = this.selectedFixedZone.JobType || this.selectedFixedZone['Job Type'];
        return jobType == selectedJobType;
      }) || null;

      this.selectedFixedFromZone = this.zoneList.find((zone) => {
        return zone['Tariff Code'] == this.selectedFixedZone['From'];
      }) || null;

      this.selectedFixedToZone = this.zoneList.find((zone) => {
        return zone['Tariff Code'] == this.selectedFixedZone['To'];
      }) || null;

      this.formMode.fixedZone = 'edit';
      this.editMode.fixedZone = true;
    },

    cancelFixedZone: function () {
      this.editMode.fixedZone = false;
      this.formMode.fixedZone = '';

      this.syncSelectedRows();
    },

    saveFixedZone: async function () {
      const mode = this.formMode.fixedZone;

      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      if (this.isHMS && !this.selectedFixedJobType) {
        this.$showAlert('warning', 'Required Fields', 'Please select Job Type.');
        return;
      }

      if (!this.selectedFixedFromZone || !this.selectedFixedToZone) {
        this.$showAlert('warning', 'Zone Required', 'Please select From Zone and To Zone.');
        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        id: String(this.selectedFixedZone['ID'] || ''),
        ...(this.isHMS ? { jobtype: String(this.selectedFixedJobType['Job Type'] || '') } : {}),
        fromarea: String(this.selectedFixedFromZone['Tariff Code'] || ''),
        toarea: String(this.selectedFixedToZone['Tariff Code'] || ''),
        driverfixedincentive: this.selectedFixedZone['Driver'],
        attendantfixedincentive: this.selectedFixedZone['Attendant'],
        ...(this.isHMS ? {
          driverotherincentive: this.selectedFixedZone['Driver Other Inc'] ?? this.selectedFixedZone['Driver %'] ?? '0',
          attendantotherincentive: this.selectedFixedZone['Attendant Other Inc'] ?? this.selectedFixedZone['Attendant %'] ?? '0',
          b2bcoeff: this.selectedFixedZone['B2B Coeff'],
        } : {}),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Fixed-Zone Incentive' : 'Updating Fixed-Zone Incentive');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveFixedZoneInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.fixedZone = false;
      this.formMode.fixedZone = '';

      await this.refreshCommissionDetails(comCode);

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteFixedZone: async function () {
      if (!this.selectedFixedZoneRows || this.selectedFixedZoneRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedFixedZoneRows[0];

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Fixed-Zone Incentive',
        'Are you sure you want to delete this Fixed-Zone Incentive?',
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: this.selectedCommission['Com Code'],
        id: String(selectedRow['ID'] || ''),
        fromarea: '',
        toarea: '',
        driverfixedincentive: '0',
        attendantfixedincentive: '0',
        driverotherincentive: '0',
        attendantotherincentive: '0',
        b2bcoeff: '0',
      };

      this.$fullscreenLoading('Deleting Fixed-Zone Incentive');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveFixedZoneInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      await this.loadCommissionDetails(this.selectedCommission);

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    editIncremental: function () {
      if (!this.selectedIncrementalRows || this.selectedIncrementalRows.length == 0) {
        return;
      }

      this.selectedIncremental = JSON.parse(JSON.stringify(this.selectedIncrementalRows[0]));

      this.selectedIncrementType = this.incrementTypeOptions.find((item) => {
        return item.label == this.selectedIncremental['IncrementType'];
      }) || this.incrementTypeOptions[0];

      this.formMode.incremental = 'edit';
      this.editMode.incremental = true;
    },

    cancelIncremental: function () {
      this.editMode.incremental = false;
      this.formMode.incremental = '';

      this.syncSelectedRows();
    },

    saveIncremental: async function () {
      const mode = this.formMode.incremental;

      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      const tripFrom = String(this.selectedIncremental['Trip From'] || '').trim();

      if (!tripFrom) {
        this.$showAlert('warning', 'Trip From Required', 'Please enter Trip From.');
        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        incentivefix: this.selectedIncremental['Driver Rate($)'],
        incentivepercent: this.selectedIncremental['Driver Rate(%)'],
        attnincentivefix: this.selectedIncremental['Attendant Rate($)'],
        attnincentivepercent: this.selectedIncremental['Attendant Rate(%)'],
        incentivepercentlocal: this.selectedIncremental['Driver Local(%)'],
        incentivepercentmedium: this.selectedIncremental['Driver Medium(%)'],
        incentivepercentoutstation: this.selectedIncremental['Driver Outstation(%)'],
        incentivefixlocal: this.selectedIncremental['Driver Local($)'],
        incentivefixmedium: this.selectedIncremental['Driver Medium($)'],
        incentivefixoutstation: this.selectedIncremental['Driver Outstation($)'],
        triprangefrom: tripFrom,
        incrementtype: String(this.selectedIncrementType?.value ?? 0),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Incremental Incentive' : 'Updating Incremental Incentive');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveAddIncrementalInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.incremental = false;
      this.formMode.incremental = '';

      await this.refreshCommissionDetails(comCode);

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteIncremental: async function () {
      if (!this.selectedIncrementalRows || this.selectedIncrementalRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedIncrementalRows[0];
      const tripFrom = String(selectedRow['Trip From'] || '').trim();

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Incremental Incentive',
        `Are you sure you want to delete Trip From <b>${tripFrom}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: this.selectedCommission['Com Code'],
        incentivefix: '0',
        incentivepercent: '0',
        attnincentivefix: '0',
        attnincentivepercent: '0',
        incentivepercentlocal: '0',
        incentivepercentmedium: '0',
        incentivepercentoutstation: '0',
        incentivefixlocal: '0',
        incentivefixmedium: '0',
        incentivefixoutstation: '0',
        triprangefrom: tripFrom,
        incrementtype: String(selectedRow['IncrementType'] ?? this.selectedIncrementType?.value ?? 0),
      };

      this.$fullscreenLoading('Deleting Incremental Incentive');

      const operationRoutePath = this.$route.path;
      const operationSystem = this.system;
      const result = await this.manageApiCall('/Manage/SaveAddIncrementalInc', requestBody);
      if (this.$route.path != operationRoutePath || this.system != operationSystem) return;

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      await this.loadCommissionDetails(this.selectedCommission);

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    incrementTypeChanged: function (selected) {
      this.selectedIncrementType = selected;

      if (this.selectedIncremental) {
        this.selectedIncremental.IncrementType = selected ? selected.value : 0;
      }
    },
    amountTypeSelected: function (cell, selected) {
      const type = selected?.label || '$';

      cell.selectedType = selected;
      cell.type = type;

      const decimals = type == '%' ? 5 : 2;

      cell.value = this.formatDecimalValue(cell.value, decimals);

      if (type == '%') {
        cell.IncentivePercent = cell.value || '0.00000';
        cell.IncentiveFix = '0.00';
      } else {
        cell.IncentiveFix = cell.value || '0.00';
        cell.IncentivePercent = '0.00000';
      }
    },
    isLastMovement: function (movement) {
      return movement == this.movementTypes[this.movementTypes.length - 1];
    },
    mapRevMvtInc: function (revDetails) {
      const jobTypes = [
        { code: 'IMP', label: 'IMPORT' },
        { code: 'EXP', label: 'EXPORT' },
        { code: '1-IMP', label: '1-WAY IMPORT' },
        { code: '1-EXP', label: '1-WAY EXPORT' },
        { code: 'IMP-EXP', label: 'IMPORT-EXPORT' },
        { code: 'MTR', label: 'EMPTY-REJECT' },
        { code: 'REPO', label: 'REPOSITION' },
        { code: '1-IMP-MT', label: '1-WAY IMP (MT)' },
        { code: '1-EXP-MT', label: '1-WAY EXP (MT)' },
      ];

      const disabledCombinations = [
        { JobType: '1-IMP', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
        { JobType: '1-EXP', MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'] },
        { JobType: 'REPO', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
        { JobType: '1-IMP-MT', MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'] },
        { JobType: '1-EXP-MT', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
      ];

      const isFixedDisabled = function (jobType, movementType) {
        return disabledCombinations.some((item) => {
          return item.JobType == jobType && item.MovementTypes.includes(movementType);
        });
      };

      return jobTypes.map((jobType) => {
        const row = {
          JobTypeCode: jobType.code,
          'Job Type': jobType.label,
        };

        this.movementTypes.forEach((movementType) => {
          const data = revDetails.find((item) => {
            return (
              String(item.JobType || '').trim() == jobType.code &&
              String(item.MovementType || '').trim() == movementType
            );
          });

          const disabled = isFixedDisabled(jobType.code, movementType);
          const incentiveType = String(data?.IncentiveType || '$').trim() || '$';
          const incentiveValue = data?.IncentiveValue || '0.00';

          row[movementType] = {
            value: disabled ? '' : incentiveValue,
            type: incentiveType,
            selectedType: { label: incentiveType },
            disabled: disabled,
            JTypeMvt: data?.JTypeMvt || `${jobType.code}${movementType}`,
            JobType: jobType.code,
            MovementType: movementType,
            IncentiveFix: data?.IncentiveFix || '0.00',
            IncentivePercent: data?.IncentivePercent || '0.00000',
          };
        });

        return row;
      });
    },
    mapDiv: function (revDetails) {
      return this.diversionTypes.map((divType) => {
        const firstDiv = revDetails.find((item) => {
          return String(item.MovementType || '').trim() == divType;
        });

        const incentiveType = String(firstDiv?.IncentiveType || '$').trim() || '$';
        const incentiveValue = firstDiv?.IncentiveValue || (incentiveType == '%' ? '0.00000' : '0.00');

        return {
          key: divType,

          value: incentiveValue,

          type: incentiveType,

          selectedType: {
            label: incentiveType,
          },
        };
      });
    },
    //#endregion

    commissionSelected: async function (selected) {
      await this.selectCommissionCode(selected);
    },
    createCommissionDetail: function (comHeader, comType, seqNo) {
      return {
        Com_Code: comHeader.Com_Code || '',
        Com_Type: comType,
        Seq_No: seqNo,
        Range: '0',
        Driver: '0',
        Attendant: '0',
        Qlf: '$',
        IncEvery: '0',
      };
    },

    mapCommissionStructure: function (result) {
      const comHeader = result && result.ComHeader ? result.ComHeader : { ComDetails: [] };

      if (!Array.isArray(comHeader.ComDetails)) {
        comHeader.ComDetails = [];
      }

      if (this.isTMS) {
        Object.assign(comHeader, {
          RangeType: String(comHeader['Range Type'] ?? comHeader.RangeType ?? '').trim(),
          CalculateBasicAttendance: Number(comHeader.CalculateBasicAttendance ?? 0),
          TripCommRate1: comHeader.TripCommRate1 ?? '0.00',
          TripCommRate2: comHeader.TripCommRate2 ?? '0.00',
          TripCommRateType1: String(comHeader.TripCommRateType1 || '$').trim(),
          TripCommRateType2: String(comHeader.TripCommRateType2 || '$').trim(),
        });

        for (let seq = 1; seq <= 4; seq++) {
          let detail = comHeader.ComDetails.find((row) => {
            return String(row.Com_Type).trim() == '0' && Number(row.Seq_No) == seq;
          });

          if (!detail) {
            detail = this.createCommissionDetail(comHeader, '0', String(seq));
            comHeader.ComDetails.push(detail);
          }

          detail.Qlf = '%';
        }
      }

      let monthlyBonus = comHeader.ComDetails.find((detail) => {
        return String(detail.Com_Type || '').trim() == '3';
      });

      if (!monthlyBonus) {
        monthlyBonus = this.createCommissionDetail(comHeader, '3', '1');
        comHeader.ComDetails.push(monthlyBonus);
      }

      let reserve = comHeader.ComDetails.find((detail) => {
        return String(detail.Com_Type || '').trim() == '4';
      });

      if (!reserve) {
        reserve = this.createCommissionDetail(comHeader, '4', '2');
        comHeader.ComDetails.push(reserve);
      }

      return {
        ComHeader: comHeader,
        monthlyBonus: monthlyBonus,
        reserve: reserve,
      };
    },

    commissionTypeSelected: function (section, selected) {
      if (!this.coefficientSettingsDetail[section]) return;

      const type = selected?.label;

      this.coefficientSettingsDetail[section].Qlf = type;

      if (section == 'monthlyBonus') {
        this.selectedMonthlyType = { label: type };
      }

      if (section == 'reserve') {
        this.selectedReserveType = { label: type };
      }
    },

    buildCommissionPayload: function () {
      const comHeader = JSON.parse(JSON.stringify(this.coefficientSettingsDetail.ComHeader || {}));
      comHeader.Com_Code = String(this.driverCommissionCode || '').trim();
      comHeader.Remark = String(this.driverCommissionDescription || '').trim();
      comHeader.ComDetails = comHeader.ComDetails || [];
      ['monthlyBonus', 'reserve'].forEach((section) => {
        const detail = this.coefficientSettingsDetail[section];
        if (!detail) return;
        const index = comHeader.ComDetails.findIndex((row) => String(row.Com_Type) == String(detail.Com_Type) && String(row.Seq_No) == String(detail.Seq_No));
        const copy = JSON.parse(JSON.stringify(detail));
        if (index >= 0) comHeader.ComDetails.splice(index, 1, copy);
        else comHeader.ComDetails.push(copy);
      });
      return { ComHeader: comHeader };
    },

    buildRevenueMovementPayload: function () {
      const payload = [];

      this.revMvtIncDet.forEach((row) => {
        this.movementTypes.forEach((movementType) => {
          const cell = row[movementType];

          if (!cell || cell.disabled) {
            return;
          }

          const type = this.getAmountType(cell) || '$';
          const value = this.formatDecimalValue(cell.value, type == '%' ? 5 : 2);

          payload.push({
            JTypeMvt: cell.JTypeMvt || `${row.JobTypeCode}${movementType}`,
            JobType: row.JobTypeCode,
            MovementType: movementType,
            IncentiveType: type,
            IncentiveValue: value,
            IncentiveFix: type == '$' ? value : '0.00',
            IncentivePercent: type == '%' ? value : '0.00000',
          });
        });
      });

      const diversionJobTypes = ['1-EXP', '1-EXP-MT', '1-IMP', 'EXP', 'IMP', 'IMP-EXP', 'MTR'];

      this.diversionDet.forEach((diversion) => {
        const type = this.getAmountType(diversion) || '$';
        const value = this.formatDecimalValue(diversion.value, type == '%' ? 5 : 2);

        diversionJobTypes.forEach((jobType) => {
          payload.push({
            JTypeMvt: `${jobType}${diversion.key}`,
            JobType: jobType,
            MovementType: diversion.key,
            IncentiveType: type,
            IncentiveValue: value,
            IncentiveFix: type == '$' ? value : '0.00',
            IncentivePercent: type == '%' ? value : '0.00000',
          });
        });
      });

      return payload;
    },

    driverCommissionSelected: async function (selected) {
      if (this.formMode.driverCommission == 'new') return;
      await this.selectCommissionCode(selected);
    },

    selectCommissionCode: async function (selected) {
      const code = String(selected?.['Com Code'] || '').trim();
      if (!code) return;

      const commission = this.commissionList.find((row) => {
        return String(row['Com Code'] || '').trim() == code;
      });
      if (!commission) return;

      this.selectedCommission = commission;
      this.selectedDriverCommission = this.driverCommissionList.find((row) => {
        return String(row['Com Code'] || '').trim() == code;
      }) || null;
      this.driverCommissionCode = code;
      this.driverCommissionDescription = commission.Remark || '';

      await this.loadCommissionDetails(commission);
    },

    setCommissionLists: function (rows) {
      this.commissionList = rows.map((row) => ({
        ...row,
        'Code - Description': String(row['Com Code'] || '').trim() + ' - ' + String(row.Remark || '').trim(),
      }));
      this.driverCommissionList = rows.map((row) => ({ ...row }));
    },

    refreshCommissionDetails: async function (comCode) {
      const commission = this.commissionList.find((row) => {
        return row['Com Code'] == comCode;
      });
      if (commission) await this.loadCommissionDetails(commission);
    },

    refreshCommissionList: async function (comCode) {
      const result = await this.getManageList('LORRYCOMISSION');

      this.setCommissionLists(result);

      const selectedCommission =
        this.commissionList.find((com) => {
          return String(com['Com Code'] || '').trim() == String(comCode || '').trim();
        }) ||
        this.commissionList[0] ||
        null;

      if (!selectedCommission) {
        this.selectedCommission = null;
        this.selectedDriverCommission = null;
        this.driverCommissionCode = '';
        this.driverCommissionDescription = '';
        this.coefficientSettingsDetail = {};
        return;
      }

      this.syncCommissionSelectors(selectedCommission['Com Code']);

      await this.loadCommissionDetails(selectedCommission);
    },

    getSelectedCommissionCode: function () {
      return String(
        this.selectedCommission?.['Com Code'] ||
          this.selectedDriverCommission?.['Com Code'] ||
          this.driverCommissionCode ||
          '',
      ).trim();
    },

    getAmountType: function (item) {
      return item?.selectedType?.label;
    },

    getAmountDecimals: function (item) {
      return this.getAmountType(item) == '%' ? 5 : 2;
    },

    getAmountValidator: function (item) {
      const decimals = this.getAmountDecimals(item);
      return `decimal,11,${decimals}`;
    },

    formatDecimalValue: function (value, decimals) {
      const rawValue = String(value || '')
        .replace(/,/g, '')
        .trim();

      if (rawValue === '') {
        return Number(0).toFixed(decimals);
      }

      const numberValue = Number(rawValue);

      if (Number.isNaN(numberValue)) {
        return Number(0).toFixed(decimals);
      }

      return numberValue.toFixed(decimals);
    },

    formatAmountCell: function (cell) {
      const decimals = this.getAmountDecimals(cell);
      cell.value = this.formatDecimalValue(cell.value, decimals);
    },

    syncCommissionSelectors: function (comCode) {
      const code = String(comCode || '').trim();

      if (!code) {
        this.selectedCommission = null;
        this.selectedDriverCommission = null;
        this.selectedSpecialDayCommission = null;
        this.driverCommissionCode = '';
        this.driverCommissionDescription = '';
        return;
      }

      const normalCommission =
        this.commissionList.find((item) => {
          return String(item['Com Code'] || '').trim() == code;
        }) || null;

      const driverCommission =
        this.driverCommissionList.find((item) => {
          return String(item['Com Code'] || '').trim() == code;
        }) || null;

      this.selectedCommission = normalCommission;
      this.selectedDriverCommission = driverCommission;
      this.selectedSpecialDayCommission = normalCommission;

      this.driverCommissionCode = code;

      this.driverCommissionDescription = normalCommission?.['Remark'] || driverCommission?.['Remark'] || '';
    },

    loadCommissionDetails: async function (selected, throwOnError = false) {
      if (!selected) return;
      const code = String(selected['Com Code'] || '').trim();
      const path = this.$route.path;
      const system = this.system;
      const loadId = (this._commissionLoadId || 0) + 1;
      this._commissionLoadId = loadId;
      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';
      this.driverCommissionBackup = null;
      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;
      this.cancelTMSFixedCommission();
      this.tableLoading = true;
      this.coefficientSettingsDetail = {};
      this.nonRevIncList = [];
      this.selectedNonRevenueRows = [];
      this.revMvtIncList = [];
      this.revMvtIncDet = [];
      this.diversionDet = [];
      this.fixedZoneIncentives = [];
      this.selectedFixedZoneRows = [];
      this.incrementalIncentives = [];
      this.selectedIncrementalRows = [];
      this.tmsFixedCommissions = [];
      this.selectedTMSFixedRows = [];
      this.$fullscreenLoading('Populating List');

      try {
        const list = (masterfile) => this.getManageList(masterfile, { action: code });
        const [fixed, incremental, details, nonRevenue, revenue, tmsFixed] = await Promise.all([
          list('GETFIXEDZONEINCENTIVES'),
          list('GETINCREMENTALINCENTIVE'),
          this.manageApiCall('/Master/GetDetailsMaster', { masterfile: 'GETCOMMISSIONDETAILS', recordid: code }),
          this.isHMS ? list('LOADFIXINC') : Promise.resolve([]),
          this.isHMS ? list('GETINCENTIVECOEFF') : Promise.resolve([]),
          this.isTMS ? list('GETTMSFIXEDCOMMISSIONS') : Promise.resolve([]),
        ]);
        if (this._commissionLoadId != loadId || this.$route.path != path || this.system != system) return;
        if (!details?.ComHeader) throw new Error('Commission header was not returned.');

        if (this.isTMS && details.ComHeader['Range Type'] == null && selected['Range Type'] != null) {
          details.ComHeader['Range Type'] = selected['Range Type'];
        }

        this.coefficientSettingsDetail = this.mapCommissionStructure(details);
        this.fixedZoneIncentives = fixed;
        this.selectedFixedZoneRows = fixed.length ? [fixed[0]] : [];
        this.incrementalIncentives = incremental;
        this.selectedIncrementalRows = incremental.length ? [incremental[0]] : [];
        this.nonRevIncList = nonRevenue;
        this.selectedNonRevenueRows = nonRevenue.length ? [nonRevenue[0]] : [];
        if (this.isHMS) {
          this.revMvtIncList = revenue;
          this.revMvtIncDet = this.mapRevMvtInc(revenue);
          this.diversionDet = this.mapDiv(revenue);
        } else {
          this.setTMSFixedCommissions(tmsFixed);
        }
        const stored = String(details.ComHeader.UseBillAmtChg || '').split(',').map((value) => value.trim().toUpperCase()).filter(Boolean);
        this.selectedUseBillAmtChg = this.isHMS ? this.billChargeOptions.filter((row) => {
          return stored.includes(String(row.SettingsItem || '').trim().toUpperCase()) ||
            stored.includes(String(row.ChgCode || '').trim().toUpperCase());
        }) : [];
        this.B2BCoeff = details.ComHeader.B2BCoeff ?? '1.0000';
        this.selectedMonthlyType = { label: this.coefficientSettingsDetail.monthlyBonus.Qlf };
        this.selectedReserveType = { label: this.coefficientSettingsDetail.reserve.Qlf };
        this.syncTMSCommissionDropdowns();
        this.syncSelectedRows();
      } catch (error) {
        if (this._commissionLoadId != loadId || this.$route.path != path || this.system != system) return;
        if (throwOnError) throw error;
        this.$showAlert('error', 'Load Failed', error.message || 'Unable to load commission details.');
      } finally {
        if (this._commissionLoadId == loadId) {
          this.tableLoading = false;
          this.$fullscreenLoading();
        }
      }
    },

    syncSelectedRows: function () {
      this.selectedNonRevenue =
        this.selectedNonRevenueRows[0] ? JSON.parse(JSON.stringify(this.selectedNonRevenueRows[0])) : {};
      this.selectedFixedZone =
        this.selectedFixedZoneRows[0] ? JSON.parse(JSON.stringify(this.selectedFixedZoneRows[0])) : {};

      this.selectedFixedJobType =
        this.jobTypeOptions.find((item) => {
          return item['Job Type'] == this.selectedFixedZone['Job Type'];
        }) || null;

      this.selectedFixedFromZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['From'];
        }) || null;

      this.selectedFixedToZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['To'];
        }) || null;

      this.selectedIncremental =
        this.selectedIncrementalRows[0] ? JSON.parse(JSON.stringify(this.selectedIncrementalRows[0])) : {};

      this.selectedIncrementType =
        this.incrementTypeOptions.find((item) => {
          return String(item.value) == String(this.selectedIncremental['IncrementType']);
        }) || this.incrementTypeOptions[0];

      this.selectedSpecialDay =
        this.selectedSpecialDayRows[0] ? JSON.parse(JSON.stringify(this.selectedSpecialDayRows[0])) : {};
      this.syncSpecialDayDetails();
    },
    getManageList: function (masterfile, options = {}) {
      return this.manageApiCall('/Master/GetListMaster', { masterfile, ...options });
    },

    manageApiCall: async function (url, body) {
      const requestBody = this.isTMS && /^\/(Master|Manage)\//.test(url)
        ? { ...(body || {}), module: 'TMS' }
        : body;
      try {
        const result = await this.$apiCallV2(url, requestBody);
        if (url.startsWith('/Manage/')) {
          if (!Array.isArray(result) || !result.length || result[0]?.Results == null) {
            return [{ Results: '1', ErrMsg: 'The server did not confirm the Manage operation.' }];
          }
          return result;
        }
        if (url.startsWith('/Master/')) {
          if (String(result?.[0]?.Results ?? result?.Results) == '1') {
            throw new Error(result?.[0]?.ErrMsg || result?.ErrMsg || 'Unable to load Manage data.');
          }
          if (url == '/Master/GetListMaster' && !Array.isArray(result)) {
            throw new Error('A list was not returned for ' + String(body?.masterfile || 'Manage') + '. Check backend support.');
          }
          if (!result) throw new Error('No response was returned for Manage data.');
        }
        return result;
      } catch (error) {
        if (url.startsWith('/Manage/')) {
          return [{ Results: '1', ErrMsg: error.message || 'Unable to save Manage data.' }];
        }
        throw error;
      }
    },
    loadManage: async function () {
      this.$fullscreenLoading('Checking Security Access');
      const sasControl = await this.manageApiCall('/SASControl/GetSASControlAccess?system=' + this.system + '&page=MNG');
      if (!Array.isArray(sasControl)) throw new Error('Security access was not returned.');

      this.menu = this.menu.map((menu) => ({ ...menu, enabled: true }));
      sasControl.forEach((setting) => {
        const menu = this.visibleMenu.find((item) => {
          const key = this.isTMS && item.title == 'Driver Incentive' ? 'SSTab1' : item.key;
          return key == setting.Control;
        });
        if (!menu) return;
        const property = String(setting.Properties || '').toLowerCase();
        const value = String(setting.PropertiesValue || '').toLowerCase() != 'false';
        if (property == 'tabenabled') menu.enabled = value;
        else menu[property] = value;
        if (!menu.enabled) menu.message = 'Access Denied';
      });

      let mountedPromises = [];
      let promisesFulfilled = 0;
      this.coefficientLoading = this.isHMS;
      this.specialDayLoading = true;

      mountedPromises.push(
        this.getManageList('LORRYCOMISSION').then((result) => {
          this.setCommissionLists(result);
          this.$topLoading(++promisesFulfilled / mountedPromises.length);
        }),
      );

      mountedPromises.push(
        this.getManageList('CHAMAREACODE').then((result) => {
          this.zoneList = result.map((zone) => ({
            ...zone,
            'Code - Description': zone['Tariff Code'] + ' - ' + zone.Area,
          }));
          this.$topLoading(++promisesFulfilled / mountedPromises.length);
        }),
      );

      mountedPromises.push(
        this.getManageList('GETSPECIALDAY').then((result) => {
          this.dayRemarkOptions = result.map((item) => ({
            ...item,
            label: item.DateRemark,
          }));
          this.$topLoading(++promisesFulfilled / mountedPromises.length);
        }),
      );

      if (this.isHMS) {
        mountedPromises.push(
          this.getManageList('GETCOEFFICIENTSETTINGS').then((result) => {
            this.priceCoefficients = this.mapPriceCoefficients(result);
            this.movementCoefficients = this.mapMovementCoefficients(result);
            this.$topLoading(++promisesFulfilled / mountedPromises.length);
          }),
        );

        mountedPromises.push(
          this.getManageList('JOBTYPE', { module: 'HMS' }).then((result) => {
            this.jobTypeOptions = result.map((item) => ({
              ...item,
              'Code - Description': item['Job Type'] + ' - ' + item.Description,
            }));
            this.$topLoading(++promisesFulfilled / mountedPromises.length);
          }),
        );

        mountedPromises.push(
          this.getManageList('GETDRIVERINCBILLCHARGES').then((result) => {
            this.billChargeOptions = result.map((item) => ({
              ...item,
              ChgCode: String(item.ChgCode || '').trim().toUpperCase(),
              SettingsItem: String(item.SettingsItem || '').trim().toUpperCase(),
            }));
            this.$topLoading(++promisesFulfilled / mountedPromises.length);
          }),
        );

        mountedPromises.push(
          Promise.all([
            this.getManageList('REVMVT'),
            this.getManageList('NONREVMVT'),
          ]).then(([revenueMovements, nonRevenueMovements]) => {
            this.specialMovementTypeOptions = [...revenueMovements, ...nonRevenueMovements].map((item) => ({
              ...item,
              'Code - Description': item.MovementType + ' - ' + item.MovementTypeDesc,
            }));
            this.$topLoading(++promisesFulfilled / mountedPromises.length);
          }),
        );
      } else {
        mountedPromises.push(
          this.getManageList('RANGETYPE').then((result) => {
            this.tmsRangeTypes = result.map((item) => ({
              ...item,
              RangeType: String(item['Range Type'] ?? item.RangeType ?? '').trim(),
            }));
            this.$topLoading(++promisesFulfilled / mountedPromises.length);
          }),
        );

        mountedPromises.push(
          this.getManageList('GETTMSMOVEMENTTYPES').then((result) => {
            this.specialMovementTypeOptions = result.map((item) => ({
              ...item,
              'Code - Description': item.MovementType + ' - ' + item.MovementTypeDesc,
            }));
            this.$topLoading(++promisesFulfilled / mountedPromises.length);
          }),
        );
      }

      const outcomes = await Promise.allSettled(mountedPromises);
      this.coefficientLoading = false;

      const failure = outcomes.find((outcome) => {
        return outcome.status == 'rejected';
      });

      if (failure) {
        throw failure.reason;
      }

      const firstCommission = this.commissionList[0] || null;

      if (firstCommission) {
        this.syncCommissionSelectors(firstCommission['Com Code']);
        await this.loadCommissionDetails(firstCommission, true);
        await this.loadSpecialDayDetails(firstCommission, true);
      } else {
        this.specialDayLoading = false;
      }

      this.syncSelectedRows();
    },
    routeChanged: async function (route) {
      const currentRoute = route || this.$route;
      const pageRoute = currentRoute.path.split('/').filter(Boolean);
      const module = pageRoute[pageRoute.length - 2];
      if (pageRoute[pageRoute.length - 1] != 'Manage' || !['Haulage', 'Trucking'].includes(module)) return;

      this._commissionLoadId = (this._commissionLoadId || 0) + 1;
      const previousLoad = this._manageRouteLoad || Promise.resolve();
      this._manageRouteLoad = previousLoad.catch(() => {}).then(async () => {
        if (this.$route.path != currentRoute.path) return;
        const defaults = this.$options.data.call(this);
        Object.keys(defaults).forEach((key) => { this[key] = defaults[key]; });
        this.system = module == 'Trucking' ? 'TMS' : 'HMS';
        await this.$nextTick();
        try {
          await this.loadManage();
        } catch (error) {
          if (this.$route.path == currentRoute.path) {
            this.$showAlert('error', 'Load Failed', error.message || 'Unable to load Manage.');
          }
        } finally {
          this.$fullscreenLoading();
          this.$topLoading(1);
        }
      });
      return this._manageRouteLoad;
    },
  },
  async mounted() {
    await this.routeChanged();
  },
  watch: {
    selectedTMSBasicPayType: function (val) {
      if (this.isTMS && this.editMode.driverCommission && this.coefficientSettingsDetail.ComHeader) {
        this.coefficientSettingsDetail.ComHeader.CalculateBasicAttendance = val ? val.value : null;
      }
    },
    selectedTMSRangeType: function (val) {
      if (this.isTMS && this.editMode.driverCommission && this.coefficientSettingsDetail.ComHeader) {
        this.coefficientSettingsDetail.ComHeader.RangeType = val ? String(val.RangeType ?? '').trim() : '';
      }
    },
    selectedTMSTripDriverType: function (val) {
      if (this.isTMS && this.editMode.driverCommission && this.coefficientSettingsDetail.ComHeader) {
        this.coefficientSettingsDetail.ComHeader.TripCommRateType1 = val ? val.label : '';
      }
    },
    selectedTMSTripAttendantType: function (val) {
      if (this.isTMS && this.editMode.driverCommission && this.coefficientSettingsDetail.ComHeader) {
        this.coefficientSettingsDetail.ComHeader.TripCommRateType2 = val ? val.label : '';
      }
    },
    selectedTMSFixedGroup: function (val) {
      if (this.isTMS && this.tmsFixedEditing) {
        this.tmsFixedForm.FixComCodeGroup = val ? val.value : '';
      }
    },
    selectedTMSFixedDriverType: function (val) {
      if (this.isTMS && this.tmsFixedEditing) {
        this.tmsFixedForm.FixComDriverQlf = val ? val.value : '';
      }
    },
    selectedTMSFixedAttendantType: function (val) {
      if (this.isTMS && this.tmsFixedEditing) {
        this.tmsFixedForm.FixComAttendantQlf = val ? val.value : '';
      }
    },

    async $route(to, from) {
      if (to.path != from.path) {
        await this.routeChanged(to);
      }
    },
    selectedNonRevenueRows: function () {
      this.syncSelectedRows();

      const selectedRow = this.selectedNonRevenueRows[0];

      if (!selectedRow) {
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
        return;
      }

      this.selectedNonRevenueTariff =
        this.tariffDropdown.find((i) => {
          return i.value == selectedRow['IncentivePercentType'];
        }) || null;

      this.selectedNonRevenueAttTariff =
        this.tariffDropdown.find((i) => {
          return i.value == selectedRow['AttnIncentivePercentType'];
        }) || null;
    },
    selectedFixedZoneRows: function () {
      this.syncSelectedRows();
    },
    selectedFixedFromZone: function (val) {
      if (this.selectedFixedZone && val) {
        this.selectedFixedZone['From'] = val['Tariff Code'];
      }
    },

    selectedFixedToZone: function (val) {
      if (this.selectedFixedZone && val) {
        this.selectedFixedZone['To'] = val['Tariff Code'];
      }
    },
    selectedIncrementalRows: function () {
      this.syncSelectedRows();
    },
    selectedSpecialDayRows: function () {
      this.syncSelectedRows();
    },
    selectedOccurrenceType: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Occur'] = val.value;
        if (val.value == 'M') {
          this.selectedSpecialDay['Day'] = String(this.selectedSpecialDayOfMonth?.value || 1);
        } else if (val.value == 'W') {
          this.selectedSpecialDay['Day'] = String(this.selectedSpecialDayOfWeek?.value || 1);
        }
      }
    },
    selectedSpecialDayOfMonth: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Day'] = String(val.value);
      }
    },
    selectedSpecialDayOfWeek: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Day'] = String(val.value);
      }
    },
    selectedDayRemark: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Day remarks'] = val.label;
      }
    },
    specialDayRemarkText: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay) {
        this.selectedSpecialDay['Day remarks'] = val;
      }
    },

    selectedSpecialMovementType: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay) {
        this.selectedSpecialDay['Mvt Type'] = val ? String(val.MovementType || '').trim() : '';
      }
    },

    selectedMovementScope: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Movement Scope'] = val.label;
        this.selectedSpecialDay['Movement Option'] = val.value;
        if (val.value == 4) {
          this.selectedSpecialDay['Mvt Type'] = '@F@';
        } else if (val.value == 0) {
          this.selectedSpecialDay['Mvt Type'] =
            this.selectedSpecialMovementType ? String(this.selectedSpecialMovementType.MovementType || '').trim() : '';
        }
      }
    },

    selectedIncentiveType: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Type'] = val.label;

        if (val.label == '%' && !this.selectedBase) {
          this.selectedBase = this.baseOptions[0];
        }
      }
    },

    selectedBase: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Base'] = val.label;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.manage-main {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  font-size: 12px;
  display: flex;
  overflow: hidden;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .manage-workspace {
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
    background: white;
    border-radius: 5px;
    padding: 5px;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .coefficient-page {
    flex: 1;
    min-height: 0;
  }

  .coefficient-page .coefficient-top {
    display: grid;
    grid-template-columns: minmax(260px, 0.8fr) minmax(520px, 1.2fr);
    gap: 5px;
    flex: 1;
    min-height: 420px;
    max-height: none;
  }

  .coefficient-page .price-panel,
  .coefficient-page .movement-panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .coefficient-page .price-panel .table-wrap,
  .coefficient-page .movement-panel .table-wrap {
    min-height: 350px;
  }

  .coefficient-page > .bottom-actions {
    margin-top: auto;
  }

  .content-header {
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 8px;
    font-weight: bolder;
    border-bottom: 2px solid rgb(180, 180, 180);
  }

  .top-selector {
    width: 280px;
    min-height: 32px;

    &.driver-commission-selector {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;

      .commission-code-input {
        width: 280px;
        flex-shrink: 0;
      }

      .commission-description-input {
        flex: 1;
        min-width: 0;
      }

      .commission-range-type {
        flex: 0 1 240px;
        min-width: 180px;
      }

      &.tms-commission-selector {
        flex-wrap: wrap;

        .commission-description-input {
          min-width: 180px;
        }
      }
    }
  }

  .driver-incentive-tab {
    flex: 1;
    min-height: 0;
  }

  .driver-tab-page {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    height: 100%;
    overflow: hidden;

    &.split-view {
      flex-direction: row;
    }
  }

  .coefficient-top {
    min-height: 290px;
    max-height: 45%;
    display: grid;
    grid-template-columns: minmax(260px, 0.8fr) minmax(520px, 1.2fr);
    gap: 5px;
  }

  .price-panel,
  .movement-panel,
  .coefficient-settings-bottom {
    min-height: 0;
  }

  .coefficient-settings-bottom {
    flex: 1.1;
  }

  .coefficient-settings-detail {
    gap: 8px;
  }

  .commission-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e9e9e9;
  }

  .fill-panel,
  .tablebox-area {
    flex: 1;
    min-height: 0;
  }

  .tablebox-area {
    overflow: hidden;
  }

  .split-view {
    flex-direction: row;
    min-height: 0;
  }

  .list-side {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    min-width: 0;
    flex: 1;
  }

  .right-menu {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    border: 1px solid #cbd3ff;
    border-radius: 5px;
    background-color: #f8f9ff;
    width: 300px;
    min-width: 300px;
    flex-shrink: 0;
    padding: 5px;
    overflow: hidden;

    ::v-deep .box-container-main {
      margin-top: 10px;
      background-color: #fff;
    }

    .bottom-actions.vertical {
      min-height: auto;
      padding: 0;
      border: none;
      background-color: transparent;

      button {
        width: 100%;
        min-height: 28px;
      }
    }
  }

  .details-panel {
    width: 100%;
    min-width: 0;
    flex: none;
  }

  .table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
    border: 1px solid #d8dcff;
    background: #fff;

    &.cipl-table-wrap {
      position: relative;
    }
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    .table-message-row {
      height: 100%;
    }

    .table-message-cell {
      height: calc(100vh - 280px);
      min-height: 300px;
      background-color: #e9e9e9;
      color: #000;
      text-align: center;
      vertical-align: top;
      padding-top: 8px;
      font-weight: normal;
      border: none;
    }

    th {
      background-color: #535cda;
      color: white;
      border: 1px solid #d8dcff;
      padding: 0 4px;
      height: 24px;
      min-height: 24px;
      text-align: center;
      white-space: nowrap;
      font-weight: 600;
      position: sticky;
      top: 0;
      z-index: 2;
    }

    td {
      border: 1px solid #d4d9ff;
      padding: 0 5px;
      height: 32px;
      min-height: 32px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    tbody tr:nth-child(odd) {
      background-color: #fff;
    }

    tbody tr:nth-child(even) {
      background-color: #e2e4ff;
    }

    tbody tr:hover {
      background-color: rgb(170, 170, 255);
    }

    input,
    select {
      width: 100%;
      min-width: 0;
      height: 24px;
      border: none;
      border-bottom: 1px solid #bfc3d6;
      background: transparent;
      font-size: 12px;
      outline: none;
    }

    input:disabled,
    select:disabled {
      color: #202020;
      opacity: 1;
    }

    &.incentive-grid {
      border-collapse: separate;
      border-spacing: 0;

      td {
        overflow: visible;
        position: relative;
        z-index: 1;
      }

      tbody tr:focus-within {
        position: relative;
        z-index: 30;
      }

      td:focus-within {
        z-index: 40;
      }
    }
  }

  .price-grid {
    th:first-child,
    td:first-child {
      width: 40px;
      text-align: center;
    }
  }

  .coefficient-grid,
  .incentive-grid {
    border-collapse: separate;
    border-spacing: 0;

    th {
      text-align: center;
    }

    td {
      overflow: visible;
      position: relative;
      z-index: 1;
    }

    tbody tr:focus-within {
      position: relative;
      z-index: 30;
    }

    td:focus-within {
      z-index: 40;
    }
  }

  .coefficient-grid {
    th:first-child,
    td:first-child {
      width: 145px;
    }
  }

  .amount-cell {
    display: grid;
    grid-template-columns: minmax(45px, 1fr) 56px;
    gap: 4px;
    align-items: center;
    position: relative;
    z-index: 1;

    &:focus-within {
      z-index: 1000;
    }
  }

  .tms-trip-commission-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .tms-trip-commission-field {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 85px;
    gap: 5px;
    align-items: end;
    min-width: 0;
  }

  .tms-revenue-inputs {
    display: flex;
    flex-direction: column;
    gap: 5px;
    overflow-x: auto;
  }

  .tms-revenue-row {
    display: grid;
    grid-template-columns: minmax(100px, 1fr) 20px minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr);
    gap: 8px;
    align-items: center;
    min-width: 580px;
  }

  @media (max-width: 1100px) {
    .tms-trip-commission-grid {
      grid-template-columns: minmax(0, 1fr);
    }

  }

  .field-grid {
    display: grid;
    gap: 5px;

    &.one {
      grid-template-columns: 1fr;
    }

    &.three {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    label {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    select {
      height: 28px;
      border: 1px solid #c8c8c8;
    }
  }

  .special-day-movement-options {
    ::v-deep .radio-buttons-container {
      flex-wrap: wrap;
      column-gap: 8px;
      row-gap: 2px;
    }
  }

  .special-day-movement-type {
    width: 100%;
    min-width: 0;
  }

  .special-day-monthly-note {
    color: #d60000;
    font-size: 10px;
    line-height: 1.25;
    white-space: normal;
    padding-top: 2px;
  }

  .details-form {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
  }

  .details-rate-row {
    display: grid;
    grid-template-columns: 70px minmax(60px, 1fr) 12px minmax(60px, 1fr) minmax(120px, 1.2fr);
    gap: 5px;
    align-items: center;

    > span {
      white-space: nowrap;
    }

    &.compact-rate {
      grid-template-columns: 85px minmax(60px, 1fr) 12px minmax(60px, 1fr);
    }
  }

  .zone-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 5px;
    width: 100%;
  }

  .formula-row {
    display: grid;
    grid-template-columns:
      auto
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr)
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr);

    align-items: end;
    gap: 5px;
    width: 100%;

    > * {
      min-width: 0;
    }

    > span {
      white-space: nowrap;
      align-self: center;
    }

    .formula-input,
    .formula-dropdown {
      width: 100%;
      min-width: 0;
    }
  }

  .diversion-row {
    display: flex;
    gap: 5px;
    margin-top: 5px;

    label {
      display: grid;
      grid-template-columns: 45px 95px 64px;
      align-items: center;
      gap: 3px;
    }
  }

  .bottom-actions {
    border: 1px solid #cbd3ff;
    border-radius: 5px;
    background-color: #f8f9ff;
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    padding: 5px;

    &.vertical {
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
    }
  }

  button {
    border: 1px solid #9aa8ee;
    background-color: #aebcff;
    color: #1c2458;
    border-radius: 3px;
    padding: 4px 10px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .details-expand-enter-active,
  .details-expand-leave-active {
    transition:
      max-height 0.3s ease,
      opacity 0.3s ease,
      transform 0.3s ease;
    overflow: hidden;
  }

  .details-expand-enter,
  .details-expand-leave-to {
    max-height: 0;
    opacity: 0;
    transform: scaleY(0);
    transform-origin: top;
  }

  .details-expand-enter-to,
  .details-expand-leave {
    max-height: 600px;
    opacity: 1;
    transform: scaleY(1);
    transform-origin: top;
  }

  .use-bill-amount-charges {
    width: 100%;
    min-height: 110px;
    margin-bottom: 10px;
  }
  
  .use-bill-amt-filter {
    width: 100%;
    height: 110px;
    margin-bottom: 10px;
  }
}
</style>
